// src/components/StructuredEditor/index.tsx
import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { App, ConfigProvider, theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
// 副作用：注册 React 19 渲染器，让 antd 静态 message/notification 在 React 19 下能弹出。
// （静态 message 仅作 App 实例未就绪时的兜底；正常路径走 <App> 的 message 实例——
//  它把 holder 渲进 React 树而非 document.body，绕开 Qiankun 沙箱代理 body 导致的不弹。）
import './antdReact19Adapter';
import type { StructuredInputProps, AgentOption } from './types';
import { extractReportUrls } from './utils/isReportUrl';
import { NodeRenderer } from './nodes';
import { SeTooltip } from './nodes/SeTooltip';
import { PopupHostContext, FullscreenOverlayHostContext } from './PopupHostContext';
import type { SkillSelection } from './footer/SkillPanel';
import { useNodeValuesSync } from './hooks/useNodeValuesSync';
import {
  useValidation,
  computeFormValid,
  hasRequiredMarkdownParseResult,
} from './hooks/useValidation';
import { useExclusion } from './hooks/useExclusion';
import { useReportAnalyze } from './hooks/useReportAnalyze';
import type { ResolveNameOutcome } from './hooks/useReportAnalyze';
import { buildFormData } from './serializer/buildFormData';
import { buildEffectiveConfig, type AnalyzeModuleStore, EMPTY_MODULE_STORE } from './utils/effectiveConfig';
import { Footer } from './footer/Footer';
import { FooterFilesList } from './footer/FooterFilesList';
import './index.css';
import { ScrollbarOverlay } from '../RichTextEditor/ScrollbarOverlay';

// agent 切换瞬间 max-height 临时放开用的安全大值，避免 editor 被旧的偏小 max-height clamp。
// 取一个远超任何输入框内容高度的值；RO 回调随后收敛到精确 scrollHeight。
const LARGE_SAFE_HEIGHT = 9999;

// 把 <App> 的 message 实例写入父级 ref，供 App 外层的 handlePaste 使用。
// 自身渲在 <App> 内 → App.useApp() 拿得到实例；不渲染任何 DOM。
const MessageBridge: React.FC<{ messageRef: React.MutableRefObject<MessageInstance | null> }> = ({ messageRef }) => {
  const { message: appMessage } = App.useApp();
  messageRef.current = appMessage;
  return null;
};

const FLOW_RADIUS = 15.5;
const FLOW_PATH_START = 16;
const FLOW_VERTICAL_LENGTH = 148;
const FLOW_CORNER_LENGTH = Math.PI * FLOW_RADIUS / 2;
const FLOW_PATH_LENGTH = 2 * (828 + FLOW_VERTICAL_LENGTH) + 2 * Math.PI * FLOW_RADIUS;
const FLOW_GAUSSIAN_MEAN_FACTOR = Math.sqrt(2 / Math.PI);
const FLOW_VISIBILITY_TRANSITION_MS = 240;
const LIGHT_FLOW_RADIUS = 12;
const LIGHT_FLOW_EDGE_OFFSET = 4;
const LIGHT_FLOW_PATH_START = LIGHT_FLOW_EDGE_OFFSET + LIGHT_FLOW_RADIUS;
const LIGHT_FLOW_VERTICAL_LENGTH = 148;
const LIGHT_FLOW_CORNER_LENGTH = Math.PI * LIGHT_FLOW_RADIUS / 2;
const LIGHT_FLOW_PATH_LENGTH = 2 * (828 + LIGHT_FLOW_VERTICAL_LENGTH) + 2 * Math.PI * LIGHT_FLOW_RADIUS;

type FlowPoint = {
  x: number;
  y: number;
  inwardX: number;
  inwardY: number;
};

type FlowLayerConfig = {
  backwardLength: number;
  forwardLength: number;
  backwardSamples: number;
  forwardSamples: number;
  triangleTop: number;
  triangleLeft: number;
  gradientStart: readonly [number, number];
  gradientEnd: readonly [number, number];
  blur: number;
};

type LightFlowLayerConfig = {
  backwardLength: number;
  forwardLength: number;
  backwardSamples: number;
  forwardSamples: number;
  strokeWidth: number;
  gradientEnd: readonly [number, number];
};

type FlowSegment = {
  length: number;
  position: number;
  width: number;
  opacity: number;
};

type FlowSegmentStyle = React.CSSProperties & {
  '--rte-flow-dash-start': string;
};

const getFlowPoint = (position: number): FlowPoint => {
  if (position >= 0) {
    return {
      x: FLOW_PATH_START + position / 100 * FLOW_PATH_LENGTH,
      y: 0.5,
      inwardX: 0,
      inwardY: 1,
    };
  }

  const backwardDistance = -position / 100 * FLOW_PATH_LENGTH;
  if (backwardDistance <= FLOW_CORNER_LENGTH) {
    const angle = -Math.PI / 2 - backwardDistance / FLOW_RADIUS;
    return {
      x: FLOW_PATH_START + FLOW_RADIUS * Math.cos(angle),
      y: FLOW_PATH_START + FLOW_RADIUS * Math.sin(angle),
      inwardX: -Math.cos(angle),
      inwardY: -Math.sin(angle),
    };
  }

  const leftDistance = backwardDistance - FLOW_CORNER_LENGTH;
  if (leftDistance <= FLOW_VERTICAL_LENGTH) {
    return {
      x: 0.5,
      y: FLOW_PATH_START + leftDistance,
      inwardX: 1,
      inwardY: 0,
    };
  }

  const cornerDistance = leftDistance - FLOW_VERTICAL_LENGTH;
  const angle = Math.PI - cornerDistance / FLOW_RADIUS;
  return {
    x: FLOW_PATH_START + FLOW_RADIUS * Math.cos(angle),
    y: 164 + FLOW_RADIUS * Math.sin(angle),
    inwardX: -Math.cos(angle),
    inwardY: -Math.sin(angle),
  };
};

const getLightFlowPoint = (position: number): FlowPoint => {
  if (position >= 0) {
    return {
      x: LIGHT_FLOW_PATH_START + position / 100 * LIGHT_FLOW_PATH_LENGTH,
      y: LIGHT_FLOW_EDGE_OFFSET,
      inwardX: 0,
      inwardY: 1,
    };
  }

  const backwardDistance = -position / 100 * LIGHT_FLOW_PATH_LENGTH;
  if (backwardDistance <= LIGHT_FLOW_CORNER_LENGTH) {
    const angle = -Math.PI / 2 - backwardDistance / LIGHT_FLOW_RADIUS;
    return {
      x: LIGHT_FLOW_PATH_START + LIGHT_FLOW_RADIUS * Math.cos(angle),
      y: LIGHT_FLOW_PATH_START + LIGHT_FLOW_RADIUS * Math.sin(angle),
      inwardX: -Math.cos(angle),
      inwardY: -Math.sin(angle),
    };
  }

  const leftDistance = backwardDistance - LIGHT_FLOW_CORNER_LENGTH;
  if (leftDistance <= LIGHT_FLOW_VERTICAL_LENGTH) {
    return {
      x: LIGHT_FLOW_EDGE_OFFSET,
      y: LIGHT_FLOW_PATH_START + leftDistance,
      inwardX: 1,
      inwardY: 0,
    };
  }

  const cornerDistance = leftDistance - LIGHT_FLOW_VERTICAL_LENGTH;
  const angle = Math.PI - cornerDistance / LIGHT_FLOW_RADIUS;
  return {
    x: LIGHT_FLOW_PATH_START + LIGHT_FLOW_RADIUS * Math.cos(angle),
    y: 164 + LIGHT_FLOW_RADIUS * Math.sin(angle),
    inwardX: -Math.cos(angle),
    inwardY: -Math.sin(angle),
  };
};

const createFlowSegments = (config: FlowLayerConfig): FlowSegment[] => {
  const gradientX = config.gradientEnd[0] - config.gradientStart[0];
  const gradientY = config.gradientEnd[1] - config.gradientStart[1];
  const gradientLengthSquared = gradientX * gradientX + gradientY * gradientY;
  const createSide = (start: number, end: number, count: number): FlowSegment[] => {
    const length = (end - start) / count;
    return Array.from({ length: count }, (_, index) => {
      const position = start + (index + 0.5) * length;
      const point = getFlowPoint(position);
      const triangleValue = point.x / config.triangleTop + point.y / config.triangleLeft;
      const depthDenominator = point.inwardX / config.triangleTop + point.inwardY / config.triangleLeft;
      const rawDepth = depthDenominator > 0
        ? Math.max(0, (1 - triangleValue) / depthDenominator)
        : triangleValue < 1
          ? 4 * config.blur
          : 0;
      const depth = Math.min(4 * config.blur, rawDepth);
      const meanDistance = Math.min(config.blur * FLOW_GAUSSIAN_MEAN_FACTOR, depth / 2);
      const midpointX = point.x + point.inwardX * meanDistance;
      const midpointY = point.y + point.inwardY * meanDistance;
      const gradientProgress = (
        (midpointX - config.gradientStart[0]) * gradientX
        + (midpointY - config.gradientStart[1]) * gradientY
      ) / gradientLengthSquared;
      const opacity = Math.max(0, Math.min(1, 1 - gradientProgress));

      return {
        length: length * 1.01,
        position,
        width: depth * 2,
        opacity,
      };
    });
  };

  return [
    ...createSide(-config.backwardLength, 0, config.backwardSamples),
    ...createSide(0, config.forwardLength, config.forwardSamples),
  ].filter((segment) => segment.width > 0.1 && segment.opacity > 0.001);
};

const createLightFlowSegments = (config: LightFlowLayerConfig): FlowSegment[] => {
  const gradientLengthSquared = config.gradientEnd[0] ** 2 + config.gradientEnd[1] ** 2;
  const createSide = (start: number, end: number, count: number): FlowSegment[] => {
    const length = (end - start) / count;
    return Array.from({ length: count }, (_, index) => {
      const position = start + (index + 0.5) * length;
      const point = getLightFlowPoint(position);
      const gradientProgress = (
        (point.x - LIGHT_FLOW_EDGE_OFFSET) * config.gradientEnd[0]
        + (point.y - LIGHT_FLOW_EDGE_OFFSET) * config.gradientEnd[1]
      ) / gradientLengthSquared;

      return {
        length: length * 1.01,
        position,
        width: config.strokeWidth,
        opacity: Math.max(0, Math.min(1, 1 - gradientProgress)),
      };
    });
  };

  return [
    ...createSide(-config.backwardLength, 0, config.backwardSamples),
    ...createSide(0, config.forwardLength, config.forwardSamples),
  ].filter((segment) => segment.opacity > 0.001);
};

const FLOW_INNER_SEGMENTS = createFlowSegments({
  backwardLength: 6.7786,
  forwardLength: 9.69535,
  backwardSamples: 14,
  forwardSamples: 20,
  triangleTop: 215,
  triangleLeft: 130,
  gradientStart: [9, -9.5],
  gradientEnd: [86.5, 101],
  blur: 5,
});

const FLOW_OUTER_SEGMENTS = createFlowSegments({
  backwardLength: 9.10954,
  forwardLength: 12.13136,
  backwardSamples: 19,
  forwardSamples: 25,
  triangleTop: 265,
  triangleLeft: 180,
  gradientStart: [9, -9.5],
  gradientEnd: [180, 165],
  blur: 12.5,
});

const LIGHT_FLOW_INNER_SEGMENTS = createLightFlowSegments({
  backwardLength: 6.607516414803234,
  forwardLength: 10.012833079774108,
  backwardSamples: 14,
  forwardSamples: 20,
  strokeWidth: 12,
  gradientEnd: [84.265, 100.627],
});

const LIGHT_FLOW_OUTER_SEGMENTS = createLightFlowSegments({
  backwardLength: 9.018926513069896,
  forwardLength: 12.479048124053445,
  backwardSamples: 19,
  forwardSamples: 25,
  strokeWidth: 12,
  gradientEnd: [94.684, 131.101],
});

type FlowVariant = 'purple' | 'blue';

const FlowSegmentBand = React.memo<{
  segments: FlowSegment[];
  color: string;
  phaseOffset: number;
  variant: FlowVariant;
}>(({ segments, color, phaseOffset, variant }) => (
  <g className={`rte-flow-border__band rte-flow-border__band--${variant}`}>
    {segments.map((segment) => {
      const dashOffset = segment.length / 2 - segment.position + phaseOffset;
      return (
        <rect
          key={segment.position}
          className="rte-flow-border__segment"
          pathLength={100}
          stroke={color}
          strokeWidth={segment.width}
          strokeOpacity={segment.opacity}
          strokeDasharray={`${segment.length} ${100 - segment.length}`}
          style={{ '--rte-flow-dash-start': String(dashOffset) } as FlowSegmentStyle}
        />
      );
    })}
  </g>
));

const FlowGlowLayer = React.memo<{
  segments: FlowSegment[];
  purpleColor: string;
  blueColor: string;
  filterId: string;
  clipId?: string;
}>(({ segments, purpleColor, blueColor, filterId, clipId }) => (
  <g filter={`url(#${filterId})`}>
    <g clipPath={clipId ? `url(#${clipId})` : undefined}>
      <FlowSegmentBand
        segments={segments}
        color={purpleColor}
        phaseOffset={0}
        variant="purple"
      />
      <FlowSegmentBand
        segments={segments}
        color={blueColor}
        phaseOffset={-50}
        variant="blue"
      />
    </g>
  </g>
));

const FlowEdgeSegmentGroup = React.memo<{
  segments: FlowSegment[];
  color: string;
  phaseOffset: number;
  strokeWidth: number;
  opacityScale: number;
}>(({ segments, color, phaseOffset, strokeWidth, opacityScale }) => (
  <g>
    {segments.map((segment) => {
      const dashOffset = segment.length / 2 - segment.position + phaseOffset;
      return (
        <rect
          key={segment.position}
          className="rte-flow-border__segment"
          pathLength={100}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeOpacity={segment.opacity * opacityScale}
          strokeDasharray={`${segment.length} ${100 - segment.length}`}
          style={{ '--rte-flow-dash-start': String(dashOffset) } as FlowSegmentStyle}
        />
      );
    })}
  </g>
));

const FlowBorderBands = React.memo(() => {
  const uniqueId = React.useId().replace(/:/g, '');
  const clipId = `rte-flow-clip-${uniqueId}`;
  const innerFilterId = `rte-flow-inner-filter-${uniqueId}`;
  const outerFilterId = `rte-flow-outer-filter-${uniqueId}`;

  return (
    <>
      <FlowGlowLayer
        segments={FLOW_INNER_SEGMENTS}
        purpleColor="#D9B6FF"
        blueColor="#BEB6FF"
        filterId={innerFilterId}
        clipId={clipId}
      />
      <FlowGlowLayer
        segments={FLOW_OUTER_SEGMENTS}
        purpleColor="#AB36FF"
        blueColor="#4D36FF"
        filterId={outerFilterId}
        clipId={clipId}
      />
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width="100%" height="100%" rx="16" ry="16" />
        </clipPath>
        <filter id={innerFilterId} x="-4%" y="-15%" width="108%" height="130%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="2" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="1.35" />
          </feComponentTransfer>
        </filter>
        <filter id={outerFilterId} x="-12%" y="-65%" width="124%" height="230%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="10" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="1.35" />
          </feComponentTransfer>
        </filter>
      </defs>
    </>
  );
});

const FlowEdgeBand = React.memo<{ variant: FlowVariant }>(({ variant }) => {
  const innerColor = variant === 'purple' ? '#D9B6FF' : '#BEB6FF';
  const outerColor = variant === 'purple' ? '#AB36FF' : '#4D36FF';
  const innerOpacityScale = variant === 'purple' ? 0.47 : 0.53;
  const outerOpacityScale = 0.62;
  const phaseOffset = variant === 'purple' ? 0 : -50;

  return (
    <g className={`rte-flow-border__band rte-flow-border__band--${variant}`}>
      <FlowEdgeSegmentGroup
        segments={FLOW_OUTER_SEGMENTS}
        color={outerColor}
        phaseOffset={phaseOffset}
        strokeWidth={1.5}
        opacityScale={outerOpacityScale}
      />
      <FlowEdgeSegmentGroup
        segments={FLOW_INNER_SEGMENTS}
        color={innerColor}
        phaseOffset={phaseOffset}
        strokeWidth={0.8}
        opacityScale={innerOpacityScale}
      />
    </g>
  );
});

const LightFlowBorderBands = React.memo(() => {
  const uniqueId = React.useId().replace(/:/g, '');
  const innerFilterId = `rte-light-flow-inner-filter-${uniqueId}`;
  const outerFilterId = `rte-light-flow-outer-filter-${uniqueId}`;

  return (
    <>
      <FlowGlowLayer
        segments={LIGHT_FLOW_INNER_SEGMENTS}
        purpleColor="#f1e6fe"
        blueColor="#DCD7FF"
        filterId={innerFilterId}
      />
      <FlowGlowLayer
        segments={LIGHT_FLOW_OUTER_SEGMENTS}
        purpleColor="#DCACFF"
        blueColor="#BBB2FD"
        filterId={outerFilterId}
      />
      <defs>
        <filter id={innerFilterId} x="-4%" y="-15%" width="108%" height="130%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="2" />
        </filter>
        <filter id={outerFilterId} x="-6%" y="-30%" width="112%" height="160%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>
    </>
  );
});

// 技能 tag：完整展示技能名（不截断、不省略号），过长时蛇形换行（渐变文字跟随换行内联流动，
// 样式见 index.css .rte-skill-inline-tag-wrap .tag-label 的 box-decoration-break:clone）。
// 默认外观与智能体 tag 一致（浅色 chip + 紫渐变文字 + 30px 行高 + hover 背景）。
// hover 上方居中气泡「点击退出技能」，与智能体 tag 的退出气泡一致（SeTooltip 深底白字）。
// 内联模式 tag（技能 / AI生图 共用）：紫渐变 chip + hover 退出气泡，点击退出。
// exitTip 默认「点击退出技能」，AI生图 传「点击退出 AI 生图」。
const SkillTag: React.FC<{ title: string; onClose: () => void; exitTip?: string }> = React.memo(({ title, onClose, exitTip = '点击退出技能' }) => (
  // zIndex 10000 盖过技能面板（SkillButton 弹层 9999），否则面板打开时 hover tag 的退出气泡
  // 被面板遮住（SeTooltip 默认 zIndex ~1070 < 9999）。与 SkillPanel 内各 tooltip 同款抬层。
  <SeTooltip title={exitTip} placement="top" zIndex={10000}>
    <span className="tag-node group" contentEditable={false}>
      <span
        className="tag-label"
        data-tag-clickable="true"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
      >
        {title}
      </span>
    </span>
  </SeTooltip>
));
SkillTag.displayName = 'SkillTag';

export const StructuredEditor: React.FC<StructuredInputProps> = (props) => {
  const {
    config,
    theme: editorTheme = 'light',
    onSend,
    isProcessing = false,
    disabled = false,
    sendDisabled = false,
    placeholder,
    nodeValues,
    onFilesChange,
    onFooterFilesChange,
    footerFiles: externalFooterFiles,
    footerFileUploadProgress,
    footerReports = [],
    onFooterReportsChange,
    onReportUrlPaste,
    footerFileUrls = [],
    onFooterFileUrlsChange,
    width,
    background,
    border,
    maxHeight,
    minHeight,
    editorMinHeight,
    editorPadding,
    className,
    videoUrlParse,
    videoUpload,
    mediaUpload,
    reportAnalyze,
    reportListFetch,
    shopListFetch,
    onReportGoGenerate,
    reportNameResolve,
    onViewReport,
    hideCurrentSessionReport,
    markdownAnalyze,
    markdownNameAnalyze,
    agentOptions,
    onAgentSelect,
    onAgentExit,
    onSkillAction,
    onSkillCardClick,
    onSkillHeaderAction,
    skillListFetch,
    showCreateSkill: showCreateSkillProp,
    createDisabled,
    skillPanelOpen,
    onSkillPanelOpenChange,
    skillPanelOpenDelay,
    skillPanelHeight,
    selectedSkill: selectedSkillProp,
    onSkillSelect,
    onAIImageClick,
    aiImageSelected,
    onAIImageExit,
    loading,
    onPause,
    canPause: externalCanPause,
    hideButtons,
    footerPadding,
    showFooterUpload,
    onAssetLibraryClick,
    // TODO(Phase 2+): lockStructure=false 模式（text 节点可编辑）暂未在 StructuredEditor 中实现。
    // 当前 Phase 1 仅接收该 prop 以保证类型兼容，不传给任何子组件、不影响行为。
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lockStructure = true,
    collapsed = false,
    onCollapsedChange,
    collapseTransitionMs = 260,
    nodeDefaults,
    // 展开态真实高度变化回调：消费端据此算对话列表底部留白，避免最后一条消息被输入框遮挡。
    onExpandedHeightChange,
    // 折叠态固定总高度（px）：传入则折叠 max-height 直接用该值，不传则内部测第一行可视高度。
    collapsedTotalHeight,
    // 点击空白处是否切换折叠态（默认 true）。消费端在非贴底等场景下可传 false 禁用该切换。
    toggleOnBlankClick = true,
    // 浮层挂载容器逃生舱：消费端（如 Qiankun 子应用有特殊布局）可强制指定浮层宿主。
    // 不传时内部用自渲染的 .rte-popup-host 兄弟节点，仍兜底 document.body。
    getPopupContainer: getPopupContainerProp,
    // 全屏遮罩类浮层（Steps 教程弹层）专属逃生舱，见 FullscreenOverlayHostContext 注释。
    getFullscreenOverlayContainer,
    // 兼容 V1 调用签名的历史 prop：V2 折叠完全由消费端 collapsed 受控，编辑器自身不监听滚动。
    // 接收但不消费（避免类型不兼容报错）。
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    scrollContainerRef,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    autoCollapseOnScroll,
  } = props;

  const isDarkTheme = editorTheme === 'dark';
  const antdThemeConfig = useMemo<ThemeConfig | undefined>(() => {
    if (!isDarkTheme) return undefined;
    return {
      algorithm: antdTheme.darkAlgorithm,
      token: {
        colorPrimary: '#A78BFA',
        colorBgBase: '#0A0A0E',
        colorBgContainer: '#151519',
        colorBgElevated: '#1C1C22',
        colorText: '#F7F7F7',
        colorTextSecondary: 'rgba(247, 247, 247, 0.68)',
        colorTextTertiary: 'rgba(247, 247, 247, 0.46)',
        colorTextPlaceholder: 'rgba(247, 247, 247, 0.36)',
        colorBorder: 'rgba(255, 255, 255, 0.16)',
        colorBorderSecondary: 'rgba(255, 255, 255, 0.1)',
        colorFillSecondary: 'rgba(255, 255, 255, 0.08)',
      },
    };
  }, [isDarkTheme]);

  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [files, setFiles] = useState<Record<string, File | string | null>>({});
  const [footerFiles, setFooterFiles] = useState<File[]>(externalFooterFiles ?? []);
  const hasUploadingFooterFiles = footerFiles.some((file) => footerFileUploadProgress?.has(file));
  const isSendBlocked = sendDisabled || hasUploadingFooterFiles;
  // 报告 chip 受控 prop 的最新值镜像：供只依赖 [config] 的切 agent 清空 effect 读取，
  // 避免把 footerReports/onFooterReportsChange 加进该 effect 的依赖改变其触发时机。
  const footerReportsRef = useRef(footerReports);
  footerReportsRef.current = footerReports;
  const onFooterReportsChangeRef = useRef(onFooterReportsChange);
  onFooterReportsChangeRef.current = onFooterReportsChange;
  const footerFileUrlsRef = useRef(footerFileUrls);
  footerFileUrlsRef.current = footerFileUrls;
  const onFooterFileUrlsChangeRef = useRef(onFooterFileUrlsChange);
  onFooterFileUrlsChangeRef.current = onFooterFileUrlsChange;
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [moduleStore, setModuleStore] = useState<AnalyzeModuleStore>(EMPTY_MODULE_STORE);
  // markdown / markdown-name 节点正在解析中的 key 集合。解析期间 effectiveConfig 隐藏原输入框、
  // 显示「解析中...」占位（与 V1 粘贴→解析 loading 体验对齐）。
  const [parsingKeys, setParsingKeys] = useState<Set<string>>(() => new Set());
  // report-url 正在解析中的 key 集合（name resolve + analyze 两个都完成后才移除）。
  // 解析期间阻止发送，避免用户在模块尚未解析完时提交不完整内容。
  const [reportUrlParsingKeys, setReportUrlParsingKeys] = useState<Set<string>>(() => new Set());
  // video-url / video-upload 正在解析或上传中的 key 集合。完成前阻止发送。
  const [videoParsingKeys, setVideoParsingKeys] = useState<Set<string>>(() => new Set());
  // report-url 解析出的报告名（按节点 key 索引）。有值时 ReportUrlNode 切到 name chip 模式。
  const [reportNames, setReportNames] = useState<Record<string, string>>({});

  // 浮层宿主：组件自渲染的 .rte-popup-host 兄弟节点（见 return）。用 state + callback ref
  // 存真实 DOM 节点，host 挂载后触发重渲染让 ConfigProvider / PopupHostContext 拿到非 null 值。
  // 「触发点定位」类浮层（技能面板/智能体下拉/ReportUrl 下拉/Steps 预览…）统一挂这里，
  // 随组件子树一起 mount/unmount → 父子关系一致 → 规避 Qiankun removeChild 崩溃，
  // 也保留与编辑器 stacking 路径同源的层级表现（业务 Modal 自然盖过来）。
  // 「全屏遮罩」类浮层（如 antd Image preview）不要挂 host：host 若被祖先 transform 困住，
  // fixed+inset:0 会缩水到祖先尺寸、盖不住视口；这类浮层走 antd 默认 document.body。
  const [popupHost, setPopupHost] = useState<HTMLElement | null>(null);
  // message(toast)专属宿主：与 popupHost 分离，且必须挂到 document.body 顶层（见下方 useLayoutEffect
  // 创建并 append）。原因：popupHost 故意停在编辑器层级(z-index 1100)好让业务 Modal 自然盖过
  // 下拉/面板；但错误 toast 是全局瞬时通知，须浮在所有弹层（含 Modal）之上。仅靠高 z-index 不够
  // ——只要编辑器祖先链上有 transform/filter 创建包含块（业务卡片常见）且自身 z-index 低，host 的
  // fixed 就被困在该祖先的 stacking context 内，body 级 Modal 仍能盖住整块。挂到 ownerDocument.body
  // 顶层即逃出所有祖先 stacking context。沙箱代理的是全局 document，节点的 ownerDocument.body 是
  // 真实可见 body，故 toast 能正常弹出且永不被遮挡。
  const [messageHost, setMessageHost] = useState<HTMLElement | null>(null);
  const editorThemeRef = useRef(editorTheme);
  editorThemeRef.current = editorTheme;
  const rootRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const ownerDoc = rootRef.current?.ownerDocument ?? document;
    const host = ownerDoc.createElement('div');
    host.className = 'rte-message-host';
    host.dataset.theme = editorThemeRef.current;
    // 关键样式 inline 写死，不依赖外部 CSS：host 被 append 到 body 顶层、可能脱离消费端的 CSS
    // 作用域隔离（qiankun 常见），命中不到 .rte-message-host 规则。inline 保证 z-index/定位永远生效。
    host.style.cssText =
      'position:fixed;inset:0;z-index:2000000000;pointer-events:none;';
    ownerDoc.body.appendChild(host);
    setMessageHost(host);
    return () => {
      host.remove();
      setMessageHost(null);
    };
  }, []);
  useEffect(() => {
    if (messageHost) messageHost.dataset.theme = editorTheme;
  }, [editorTheme, messageHost]);
  const resolvePopupContainer = useCallback(
    () => getPopupContainerProp?.() ?? popupHost ?? document.body,
    [getPopupContainerProp, popupHost],
  );

  // <App> 的 message 实例桥接：顶层组件自身（如 handlePaste）在 <App> 外层，拿不到
  // App.useApp() 的 context，故用 ref 由内层 MessageBridge 写入，paste handler 经 ref 调用。
  // 子组件（SkillPanel/UploadNode 等）都在 <App> 后代，直接各自 App.useApp() 即可。
  const messageRef = useRef<MessageInstance | null>(null);

  // 当前处于「全屏展开」态的 richtext 节点 key（null=无）。对齐 V1 覆盖式展开：
  // 该 richtext 撑满编辑区、其余节点隐藏。V2 受控叶子够不到兄弟节点，故状态提到父级。
  const [fullscreenRichtextKey, setFullscreenRichtextKey] = useState<string | null>(null);
  const onRichtextExpandChange = useCallback((key: string, expanded: boolean) => {
    setFullscreenRichtextKey(expanded ? key : null);
  }, []);

  // ── collapsed 折叠/展开过渡（对齐 V1 collapseBehavior）──────────────────────
  // 关键：max-height 两端都必须是具体像素值，否则 collapsed(px) → 展开(auto/none)
  // 无法做 CSS transition，会瞬间跳开。故折叠态测「第一行可视高度」、展开态测 scrollHeight。
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const [collapsedMaxHeightPx, setCollapsedMaxHeightPx] = useState<number>(58);
  const [expandedMaxHeightPx, setExpandedMaxHeightPx] = useState<number>(58);
  // 内容是否真超出可视区（scrollHeight > clientHeight）。放得下时 overflow:hidden → editor
  // agent 切换（内容整体替换）期间临时禁 transition：fingerprint 变化时 expanded 被放开成
  // 9999、RO 再收敛到真实高度，这一来一回若走插值会「先猛涨再回落」可见跳动。agent 切换不是
  // 用户的展开/收起手势，不该有动画。置 true 后短窗口（一个动画周期）自动复位，期间 max-height
  // 直接到位、无动画；窗口结束恢复常驻 transition，用户主动 collapsed 切换照常平滑。
  const [disableTransition, setDisableTransition] = useState(false);
  // collapsed 翻转后的「折叠动画进行中」窗口：动画期间 measureHeights 绝不摘 inline max-height
  // （摘成 'none' 会让浏览器丢失 max-height 动画起点 → 瞬跳）。收起方向靠 `!collapsed` 守卫即可
  // 跳过测 expanded，但展开方向 collapsed=false、RO 在动画中持续触发摘 cap → 展开瞬跳。故需独立
  // 窗口标志，双向都在动画周期内禁摘 cap。用 ref（不触发重渲染）+ 时间戳，measureHeights 同步读。
  const collapseAnimUntilRef = useRef<number>(0);
  const prevCollapsedForAnimRef = useRef<boolean>(collapsed);
  // 动画期间强制 overflow:hidden（render 可见，故用 state）：展开方向 collapsed 立即=false →
  // overflow 立即切 auto，但 max-height 还在 46→131 动画中，中间帧 max-height < scrollHeight →
  // auto 闪出滚动条直到动画结束。翻转那帧同步置 true（overflow 当帧即 hidden、无闪现），窗口结束复位。
  const [collapseAnimating, setCollapseAnimating] = useState(false);
  if (prevCollapsedForAnimRef.current !== collapsed) {
    prevCollapsedForAnimRef.current = collapsed;
    collapseAnimUntilRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now()) + collapseTransitionMs;
    setCollapseAnimating(true);
  }
  // 动画窗口结束复位 collapseAnimating → overflow 恢复 auto（展开态内容超长可内部滚动）。
  useEffect(() => {
    if (!collapseAnimating) return;
    const t = setTimeout(() => setCollapseAnimating(false), collapseTransitionMs);
    return () => clearTimeout(t);
  }, [collapseAnimating, collapsed, collapseTransitionMs]);

  /** 默认 empty 模式（config=[]）的纯文本。整个输入区是一个受控原生 textarea，
   *  用户输入纯文本，发送时调用 onSend(text, text)。
   *  受控（value/onChange）即可，光标由浏览器原生维护、不会跳到开头。 */
  const [defaultText, setDefaultText] = useState<string>('');
  const defaultTextareaRef = useRef<HTMLTextAreaElement>(null);
  // 内嵌技能 tag 的测量容器：tag 蛇形换行可能占多行，需测「末行右端」(text-indent)
  // 和「多出行数高度」(padding-top) 两个量，让 textarea 文本接在 tag 末行之后、不与上几行重叠。
  const skillTagRef = useRef<HTMLSpanElement>(null);
  // 技能 tag 末行右端缩进（px）：textarea 首行 text-indent，让光标接在 tag 末行之后。
  const [skillTagWidth, setSkillTagWidth] = useState(0);
  // 技能 tag 多出的行数对应的高度（px）：textarea padding-top，把首行下推到 tag 末行同一行
  // （tag 蛇形换行占多行时，上面几行由绝对定位覆盖在 textarea 顶部留白区，不与文本重叠）。
  const [skillTagPadTop, setSkillTagPadTop] = useState(0);

  // Sync external footerFiles prop changes to internal state (avoid re-emit loop via ref).
  // I1: bound by 50MB total — if external list overflows, filter and let onFooterFilesChange
  //     回传过滤后的列表（不设 externalSyncRef）。
  // I2: per-element shallow equality (name + size + lastModified) — avoid re-set on parent rerender.
  const externalSyncRef = useRef(false);
  useEffect(() => {
    if (!externalFooterFiles) return;
    const isSame =
      externalFooterFiles.length === footerFiles.length &&
      externalFooterFiles.every((f, i) => {
        const cur = footerFiles[i];
        return cur && f.name === cur.name && f.size === cur.size && f.lastModified === cur.lastModified;
      });
    if (isSame) return;

    const maxTotal = 50 * 1024 * 1024;
    const total = externalFooterFiles.reduce((s, f) => s + f.size, 0);
    if (total > maxTotal) {
      const accepted: File[] = [];
      let sum = 0;
      for (const f of externalFooterFiles) {
        if (sum + f.size > maxTotal) break;
        accepted.push(f);
        sum += f.size;
      }
      // 不设 externalSyncRef：让 onFooterFilesChange useEffect 把过滤后的列表回传给父组件
      setFooterFiles(accepted);
    } else {
      externalSyncRef.current = true;
      setFooterFiles(externalFooterFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFooterFiles]);

  const effectiveConfig = useMemo(
    () => buildEffectiveConfig(config, moduleStore, parsingKeys),
    [config, moduleStore, parsingKeys],
  );

  // 切换智能体时清空普通对话区上传的 footerFiles。
  // 身份用「第一个 tag 节点的 text」标识（对齐 V1 agentStateSync.getAgentId）：普通对话(无 tag)→
  // 选智能体、智能体间互切、退出智能体都算身份变化，残留的 footer 文件会跟着新上下文一起发送、语义错乱。
  // 只在身份真正变化时清，同一智能体内 config 引用变动（解析展开等）不触发。setFooterFiles([]) 会经
  // 既有回传 effect 通知受控父组件。注：V1 仅在发送成功后清 footer、切换不清——此处按产品诉求修正该行为。
  // 用独立 initialized 标志而非「prevId===null」判定首次：普通对话的 agentId 本身就是 null，
  // 不能与「未初始化」混淆，否则普通对话→选智能体那次会被当首次跳过、漏清。
  const prevAgentIdRef = useRef<string | null>(null);
  const agentIdInitedRef = useRef(false);
  useEffect(() => {
    const agentId = config.find((n) => n.type === 'tag')?.text ?? null;
    if (!agentIdInitedRef.current) {
      agentIdInitedRef.current = true;
      prevAgentIdRef.current = agentId;
      return;
    }
    if (prevAgentIdRef.current !== agentId) {
      prevAgentIdRef.current = agentId;
      setFooterFiles((prev) => (prev.length === 0 ? prev : []));
      // 报告 chip 受控：切换身份时通知消费端清空（避免残留报告跟着新上下文发送）。
      if (footerReportsRef.current.length) onFooterReportsChangeRef.current?.([]);
      // 已上传文件卡片受控：同上清空。
      if (footerFileUrlsRef.current.length) onFooterFileUrlsChangeRef.current?.([]);
    }
  }, [config]);


  // 当前选中智能体：V1 parity——从 config 的 tag 节点 text 反查 agentOptions（不依赖外部 state）。
  // 用于底部积分徽标渲染（points / 限时免费）。
  const currentAgent = useMemo(() => {
    const tagText = config.find((n) => n.type === 'tag')?.text;
    if (!tagText || !agentOptions) return undefined;
    return agentOptions.find((a) => a.label === tagText);
  }, [config, agentOptions]);

  // 智能体态判据：config 里存在 type:'tag' 节点即视为已激活智能体。
  // 不复用 currentAgent——后者还要求 agentOptions 匹配成功，消费端没传 agentOptions
  // 或 label 对不上时会误判为非智能体态，导致「基于会话创建技能」按钮错误显示。
  const isAgentActive = useMemo(
    () => config.some((n) => n.type === 'tag'),
    [config],
  );

  // 「基于会话创建技能」显隐：库分不清「首页入口」和「连续对话页」（两者都非智能体态），
  // 故优先用消费端传入的 showCreateSkill（连续对话页传 true、首页传 false）；
  // 消费端未传时回退库内 !isAgentActive（智能体态隐藏）作为安全默认。
  const showCreateSkill = showCreateSkillProp ?? !isAgentActive;

  useNodeValuesSync(nodeValues, values, setValues, effectiveConfig, files, setFiles);

  // ref 和 fingerprint 声明提前（effect 移到 onReportUrlParse/onResolveMultiName 定义之后）
  const reportUrlBackfillDoneRef = useRef<Map<string, string>>(new Map());
  const prevConfigFingerprintForReportRef = useRef('');
  const reportFirstTagText = (config.find(n => n.type === 'tag') as { text?: string } | undefined)?.text || '';
  const reportConfigFingerprint = reportFirstTagText + '||' + config
    .filter(n => n.key && n.type !== 'report-module' && n.type !== 'markdown-module')
    .map(n => `${n.type}:${n.key}`)
    .join('|');
  if (prevConfigFingerprintForReportRef.current !== reportConfigFingerprint) {
    prevConfigFingerprintForReportRef.current = reportConfigFingerprint;
    reportUrlBackfillDoneRef.current = new Map();
  }

  // 当前选中技能（V2 新增，全 V2 自持）。与智能体、AI生图 三者互斥：选一个清另两个。
  const [selectedSkill, setSelectedSkill] = useState<SkillSelection | null>(null);
  // AI生图 模式（库内自持）：激活时 config=[] 下渲染「AI生图」tag + 普通输入框。与智能体/技能互斥。
  const [aiImageActive, setAiImageActive] = useState(false);

  // 外部 selectedSkill prop 变化时同步到内部选中状态（等同于用户点了「去使用」）
  useEffect(() => {
    if (selectedSkillProp === undefined) return;
    setSelectedSkill(selectedSkillProp);
  }, [selectedSkillProp]);

  // 外部 aiImageSelected prop 受控同步（不传则纯由内部 handleAIImageClick / tag× 管理）
  useEffect(() => {
    if (aiImageSelected === undefined) return;
    setAiImageActive(aiImageSelected);
  }, [aiImageSelected]);

  // 选/取消技能：选中时清掉已选智能体（onAgentExit('skill-switch') 移除 tag）+ 清 AI生图，实现「选一个清另一个」。
  // 传 'skill-switch' 让消费端知道这是切技能而非真退出 → 不要 remount（保住刚 set 的 selectedSkill）。
  const handleSkillSelect = useCallback(
    (skill: SkillSelection | null) => {
      setSelectedSkill(skill);
      if (skill) setAiImageActive(false);
      onSkillSelect?.(skill);
      if (skill && currentAgent) onAgentExit?.('skill-switch');
    },
    [currentAgent, onAgentExit, onSkillSelect],
  );

  // 选智能体：清技能 + 清 AI生图（互斥另一向），再走消费端 onAgentSelect 真正切换。
  const handleAgentSelect = useCallback(
    (agent: AgentOption) => {
      setSelectedSkill(null);
      setAiImageActive(false);
      // 同 handleAIImageClick：库内清技能要通知受控消费端，防止旧技能 prop 被同步 effect 灌回。
      onSkillSelect?.(null);
      onAgentSelect?.(agent);
    },
    [onAgentSelect, onSkillSelect],
  );

  // 点底部「AI生图」按钮：进入 AI生图 模式（编辑器顶部 tag 变「AI生图」）。互斥——清技能 + 退出当前智能体
  // （onAgentExit('ai-image-switch') 让消费端清 config tag 但别 remount，保住 aiImageActive，语义同 skill-switch）。
  const handleAIImageClick = useCallback(() => {
    setAiImageActive(true);
    setSelectedSkill(null);
    // 通知受控消费端清掉其 selectedSkill——否则消费端仍传旧技能 prop，
    // 会被上面 selectedSkillProp 同步 effect 重新灌回库内，退出 AI生图 后 placeholder 残留技能。
    onSkillSelect?.(null);
    if (currentAgent) onAgentExit?.('ai-image-switch');
    onAIImageClick?.();
  }, [currentAgent, onAgentExit, onAIImageClick, onSkillSelect]);

  // 底部按钮 × 退出：与编辑器内 tag 的退出同一路径 → 状态由同一处清除，双向同步。
  // 智能体：同 tag-close（消费端据此移除 config tag，currentAgent 随之变 undefined）。
  const handleAgentExit = useCallback(() => onAgentExit?.('tag-close'), [onAgentExit]);
  // AI生图：同编辑器 AI生图 tag 的 onClose（清库内 aiImageActive + 通知消费端）。
  const handleAIImageExit = useCallback(() => {
    setAiImageActive(false);
    onAIImageExit?.();
  }, [onAIImageExit]);

  // 互斥安全网：智能体也可能被消费端直接改 config tag 选中（不走 handleAgentSelect）。
  // 一旦 currentAgent 出现，强制清掉已选技能 + AI生图。注：选技能/AI生图 时 currentAgent 尚为旧值不触发，
  // onAgentExit 把它清成 undefined 后此处 if 守卫不再清，两向互斥不打架。
  useEffect(() => {
    if (currentAgent) {
      setSelectedSkill(null);
      setAiImageActive(false);
    }
  }, [currentAgent]);

  const { errors, validate, clearError } = useValidation(effectiveConfig);
  const { excludedKeys, hiddenSeparatorIndexes, conditionalHiddenIndexes } = useExclusion(effectiveConfig, values, files);

  const { analyzeReport, analyzeMarkdown, analyzeMarkdownName, resolveReportName } = useReportAnalyze({
    reportAnalyze,
    markdownAnalyze,
    markdownNameAnalyze,
    reportNameResolve,
  });

  // contentEditable 清空后常残留 <br> / &nbsp; / 零宽字符，统一判空
  const isHtmlBlank = (v: string | string[] | undefined): boolean => {
    if (typeof v !== 'string') return !v;
    return v
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, '')
      .replace(/\s/g, '')
      .replace(/[​‌‍﻿]/g, '')
      .trim() === '';
  };

  const onChangeValue = useCallback((key: string, v: string | string[]) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    clearError(key);

    // markdown-module 表格被清空 → 若该父节点下所有模块都空了，移除 moduleStore 里的模块，
    // effectiveConfig 随即把原输入框恢复出来（V1 parity：清空表格回到粘贴态）。
    const moduleMatch = /^(.+)__module_\d+$/.exec(key);
    if (moduleMatch && isHtmlBlank(v)) {
      const parentKey = moduleMatch[1];
      const parentNode = effectiveConfig.find(
        (n) => n.key === parentKey && (n.type === 'markdown-module'),
      );
      if (parentNode) {
        setValues((prev) => {
          const next = { ...prev, [key]: v };
          const allBlank = Object.keys(next)
            .filter((k) => k.startsWith(`${parentKey}__module_`))
            .every((k) => isHtmlBlank(next[k]));
          if (allBlank) {
            setModuleStore((s) => {
              if (!s.markdownModules[parentKey]) return s;
              const md = { ...s.markdownModules };
              delete md[parentKey];
              return { ...s, markdownModules: md };
            });
            Object.keys(next)
              .filter((k) => k.startsWith(`${parentKey}__module_`))
              .forEach((k) => delete next[k]);
            // 恢复的输入框读 values[parentKey]，若不清掉会回填原粘贴文本 → 框里残留旧数据
            next[parentKey] = '';
          }
          return next;
        });
      }
      return;
    }

    // I4: when a parse-parent node is cleared, remove its modules from the store
    if (typeof v === 'string' && v.trim() === '') {
      const node = effectiveConfig.find((n) => n.key === key);
      if (node?.type === 'report-url') {
        setModuleStore((prev) => {
          if (!prev.reportModules[key]) return prev;
          const next = { ...prev.reportModules };
          delete next[key];
          return { ...prev, reportModules: next };
        });
        // 清空 report-url 时同步清掉已解析的报告名（chip 退回 URL 输入模式）
        setReportNames((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } else if (node?.type === 'markdown' || node?.type === 'markdown-name') {
        setModuleStore((prev) => {
          if (!prev.markdownModules[key]) return prev;
          const next = { ...prev.markdownModules };
          delete next[key];
          return { ...prev, markdownModules: next };
        });
      }
    }
  }, [clearError, effectiveConfig]);

  const onChangeFile = useCallback(
    (key: string, file: File | string | null) => {
      setFiles((prev) => ({ ...prev, [key]: file }));
    },
    [],
  );

  const onThumbnailChange = useCallback(
    (key: string, thumbnail: string) => setThumbnails((prev) => ({ ...prev, [key]: thumbnail })),
    [],
  );

  // report-url name chip 清除：清掉 value（onChangeValue 会连带清 moduleStore + reportNames），
  // 此处显式再清一次 reportNames 保证幂等（chip 立即退回 URL 输入模式）。
  // multi-variable / multi-variable-select slot 清除时，数组会 splice 前移，需要把后续 slot
  // 的 reportName 也前移一位（key 格式为 `${nodeKey}_${idx}`）。
  const handleClearReport = useCallback((key: string) => {
    setReportNames((prev) => {
      if (!(key in prev)) return prev;
      const match = key.match(/^(.+)_(\d+)$/);
      if (!match) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      const [, nodeKey, idxStr] = match;
      const deletedIdx = parseInt(idxStr, 10);
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (k === key) continue;
        const m = k.match(/^(.+)_(\d+)$/);
        if (m && m[1] === nodeKey) {
          const i = parseInt(m[2], 10);
          if (i > deletedIdx) {
            next[`${nodeKey}_${i - 1}`] = v;
          } else {
            next[k] = v;
          }
        } else {
          next[k] = v;
        }
      }
      return next;
    });
  }, []);

  // multi-variable / multi-variable-select 槽位前移/删除时同步重排 reportNames（V1 inputHandler.ts:412-419）。
  // reportNames 以 `${nodeKey}_${idx}` 为键，但清空中间槽会 splice 前移数组 → index 变了，
  // 名字键却停在旧 index → 报告名串到错误 slot / 丢失（用户：混合输入删链接出错）。
  // 单一操作覆盖两种清空：① splice 前移（删 removedIdx，更高索引名字下移一位）；
  // ② 尾部截断（删 removedIdx，更高索引本就是空槽无名字 → 下移为 no-op）。
  const handleShiftReportNames = useCallback((nodeKey: string, removedIdx: number) => {
    setReportNames((prev) => {
      const prefix = `${nodeKey}_`;
      // 收集该 nodeKey 下所有 (idx, name)
      const entries: Array<[number, string]> = [];
      for (const k of Object.keys(prev)) {
        if (!k.startsWith(prefix)) continue;
        const suffix = k.slice(prefix.length);
        if (!/^\d+$/.test(suffix)) continue;
        entries.push([Number(suffix), prev[k]]);
      }
      if (entries.length === 0) return prev;
      const next = { ...prev };
      for (const [i] of entries) delete next[`${nodeKey}_${i}`];
      for (const [i, name] of entries) {
        if (i === removedIdx) continue; // 被删槽：丢弃
        const newIdx = i > removedIdx ? i - 1 : i;
        next[`${nodeKey}_${newIdx}`] = name;
      }
      return next;
    });
  }, []);

  // multi-variable / multi-variable-select slot 失焦：内容为 URL 时解析报告名，写入 reportNames[itemKey]。
  // itemKey 形如 `${nodeKey}_${idx}`，与 ChipRow 渲染时读取的索引一致。
  // 返回 outcome 给 ChipRow：success 这里直接落 reportNames；failed/skip/stale 交给 ChipRow
  // 决定是否清空 slot + 弹错（清空要走 node 自己的数组 compaction，故不在此处理）。
  const onResolveMultiName = useCallback(
    async (
      itemKey: string,
      url: string,
      nodeType: 'multi-variable' | 'multi-variable-select',
    ): Promise<ResolveNameOutcome> => {
      const outcome = await resolveReportName(itemKey, url, nodeType);
      if (outcome.status === 'success') {
        setReportNames((prev) => ({ ...prev, [itemKey]: outcome.name }));
      }
      return outcome;
    },
    [resolveReportName],
  );

  // multi-variable-select 选报告后触发模块解析（V1 数据副作用：拉报告模块进 moduleStore，
  // 但 multi-variable-select 不展开渲染 module，仅供下游消费 / 与 V1 行为对齐）。
  const onAnalyzeMultiReport = useCallback(
    (itemKey: string, url: string) => {
      analyzeReport(itemKey, url).then((outcome) => {
        if (outcome.status === 'success') {
          setModuleStore((prev) => ({
            ...prev,
            reportModules: { ...prev.reportModules, [itemKey]: outcome.modules },
          }));
        }
      });
    },
    [analyzeReport],
  );

  const onReportUrlParse = useCallback(
    async (key: string, url: string) => {
      setReportUrlParsingKeys((prev) => { const next = new Set(prev); next.add(key); return next; });
      try {
        const namePromise = resolveReportName(key, url, 'report-url').then((res) => {
          if (res.status === 'success') {
            setReportNames((prev) => ({ ...prev, [key]: res.name }));
          }
        });
        const analyzePromise = analyzeReport(key, url).then((outcome) => {
          if (outcome.status === 'success') {
            setModuleStore((prev) => ({
              ...prev,
              reportModules: { ...prev.reportModules, [key]: outcome.modules },
            }));
            setValues((prev) => {
              const next = { ...prev };
              outcome.modules.forEach((m, i) => {
                next[`${key}__module_${i}`] = m.content;
              });
              return next;
            });
          } else if (outcome.status === 'failed') {
            onChangeValue(key, '');
            setValues((prev) => {
              let changed = false;
              const next = { ...prev };
              for (const k of Object.keys(next)) {
                if (k.startsWith(`${key}__module_`)) {
                  delete next[k];
                  changed = true;
                }
              }
              return changed ? next : prev;
            });
          }
        });
        await Promise.all([namePromise, analyzePromise]);
      } finally {
        setReportUrlParsingKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev); next.delete(key); return next;
        });
      }
    },
    [analyzeReport, resolveReportName, onChangeValue],
  );

  const onVideoParsingChange = useCallback((key: string, parsing: boolean) => {
    setVideoParsingKeys((prev) => {
      const next = new Set(prev);
      if (parsing) next.add(key); else next.delete(key);
      if (next.size === prev.size && [...next].every((k) => prev.has(k))) return prev;
      return next;
    });
  }, []);

  // nodeValues 回填 report-url / multi-variable-select 时自动触发解析
  useEffect(() => {
    if (!nodeValues || config.length === 0) return;
    const reportUrlNodes = config.filter((n) => n.type === 'report-url' && n.key);
    const multiSelectNodes = config.filter((n) => n.type === 'multi-variable-select' && n.key);
    if (reportUrlNodes.length === 0 && multiSelectNodes.length === 0) return;

    for (const node of reportUrlNodes) {
      const key = node.key!;
      const val = nodeValues[key] ?? nodeValues[`${key}__url`];
      if (!val || (typeof val === 'string' && !val.trim())) continue;
      const url = Array.isArray(val) ? String(val[0] ?? '') : String(val);
      if (!url) continue;
      if (reportUrlBackfillDoneRef.current.get(key) === url) continue;
      reportUrlBackfillDoneRef.current.set(key, url);
      setTimeout(() => { onReportUrlParse(key, url); }, 100);
    }

    for (const node of multiSelectNodes) {
      const key = node.key!;
      const val = nodeValues[key];
      if (!Array.isArray(val) || val.length === 0) continue;
      val.forEach((raw, idx) => {
        const url = typeof raw === 'string' ? raw.trim() : '';
        if (!url) return;
        const childKey = `${key}_${idx}`;
        if (reportUrlBackfillDoneRef.current.get(childKey) === url) return;
        reportUrlBackfillDoneRef.current.set(childKey, url);
        setTimeout(() => {
          onResolveMultiName(childKey, url, 'multi-variable-select');
        }, 100);
      });
    }
  }, [nodeValues, config, onReportUrlParse, onResolveMultiName]);

  // markdown / markdown-name 解析共用：标记 parsing → 调用 analyze（保证 loading 至少
  // 显示 MIN_LOADING_MS，避免一闪而过）→ 写 moduleStore + seed 模块内容到 values → 清 parsing。
  // 解析失败（analyze 返回 null）时也清 parsing，原输入框回到可编辑态供用户重试。
  const runMarkdownParse = useCallback(
    async (
      key: string,
      text: string,
      analyze: (k: string, t: string) => Promise<{ modules: Array<{ title: string; content: string; markdown: string }> } | null>,
    ) => {
      const MIN_LOADING_MS = 500;
      const startedAt = Date.now();
      // 输入再次触发解析时，旧模块已经不再代表当前文本。先失效旧结果，避免新值
      // 解析失败期间继续展示并提交上一家店铺/上一份表格。
      setModuleStore((prev) => {
        if (!prev.markdownModules[key]) return prev;
        const next = { ...prev.markdownModules };
        delete next[key];
        return { ...prev, markdownModules: next };
      });
      setValues((prev) => {
        const next = { ...prev };
        Object.keys(next)
          .filter((valueKey) => valueKey.startsWith(`${key}__module_`))
          .forEach((valueKey) => delete next[valueKey]);
        return next;
      });
      setParsingKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      try {
        const r = await analyze(key, text);
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_LOADING_MS) {
          await new Promise((res) => setTimeout(res, MIN_LOADING_MS - elapsed));
        }
        if (r) {
          setModuleStore((prev) => ({
            ...prev,
            markdownModules: { ...prev.markdownModules, [key]: r.modules },
          }));
          // 把解析出的模块内容预填进 values，编辑区直接可改
          setValues((prev) => {
            const next = { ...prev };
            r.modules.forEach((m, i) => {
              next[`${key}__module_${i}`] = m.content;
            });
            return next;
          });
        }
      } finally {
        setParsingKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [],
  );

  const onMarkdownParse = useCallback(
    (key: string, text: string) => runMarkdownParse(key, text, analyzeMarkdown),
    [runMarkdownParse, analyzeMarkdown],
  );

  const onMarkdownNameParse = useCallback(
    (key: string, text: string) => runMarkdownParse(key, text, analyzeMarkdownName),
    [runMarkdownParse, analyzeMarkdownName],
  );
  useEffect(() => {
    onFilesChange?.(files);
  }, [files, onFilesChange]);

  useEffect(() => {
    if (externalSyncRef.current) {
      externalSyncRef.current = false;
      return;
    }
    onFooterFilesChange?.(footerFiles);
  }, [footerFiles, onFooterFilesChange]);

  const handleSend = useCallback(async () => {
    if (
      isProcessing
      || loading
      || disabled
      || isSendBlocked
      || parsingKeys.size > 0
      || reportUrlParsingKeys.size > 0
      || videoParsingKeys.size > 0
    ) return;
    // 有报告链接仍在解析时阻止发送（避免漏带尚未确认的报告）。
    if (footerReports.some((r) => r.status === 'parsing')) return;
    // 当前激活模式标识：智能体=1 / 技能=2 / AI生图=3（三者互斥，一次只激活一个）；普通对话不带。
    // 智能体态判据用 isAgentActive（config 有 tag 节点，不依赖 agentOptions 是否匹配）。
    const generateImage: 1 | 2 | 3 | undefined = isAgentActive ? 1 : selectedSkill ? 2 : aiImageActive ? 3 : undefined;
    // 选中技能时带 skillCode + skillName；generateImage 有值时一并带上。普通对话两者皆无 → meta undefined。
    const sendMeta =
      generateImage === undefined
        ? undefined
        : {
          ...(selectedSkill ? { skillCode: selectedSkill.id, skillName: selectedSkill.title } : {}),
          generateImage,
        };
    // 默认 empty 模式（config=[]）：纯文本路径
    if (config.length === 0) {
      const text = defaultText.trim();
      // 已解析成功的报告链接（发送时以 url 追加到 reportUrls 字段）
      const doneReports = footerReports.filter((r) => r.status === 'done');
      if (!text && !footerFiles.length && !doneReports.length && !footerFileUrls.length) return;
      // 选中技能时，fullPrompt 只包含用户输入的文本，技能信息通过 meta.skillName 传递
      const fullPrompt = text;
      if (footerFiles.length || doneReports.length || footerFileUrls.length) {
        // 有附件/报告/已上传文件时构造 FormData，与结构化模式对齐。
        // 上传文件（二进制）走 files；报告链接走 reportUrls；已上传文件的 OSS URL 走 fileUrls。
        // 三者都是同名 key 重复 append → 后端收成数组（后端约定：整条 url 字符串）。
        const formData = new FormData();
        if (fullPrompt) formData.append('prompt', fullPrompt);
        footerFiles.forEach((f) => formData.append('files', f));
        doneReports.forEach((r) => formData.append('reportUrls', r.url));
        footerFileUrls.forEach((f) => formData.append('fileUrls', f.url));
        onSend(formData, fullPrompt, sendMeta);
      } else {
        onSend(text, fullPrompt, sendMeta);
      }
      setDefaultText(''); // 发送后清空输入框（V1 parity：回车发送即清空）
      setFooterFiles([]); // 发送后清空附件
      if (footerReports.length) onFooterReportsChange?.([]); // 发送后清空报告 chip（受控，回传消费端）
      if (footerFileUrls.length) onFooterFileUrlsChange?.([]); // 发送后清空已上传文件卡片（受控）
      return;
    }
    if (!hasRequiredMarkdownParseResult(config, moduleStore.markdownModules, excludedKeys)) return;
    if (!validate(values, files, excludedKeys)) return;
    const { formData, fullPrompt, hasContent } = await buildFormData({
      config: effectiveConfig, values, files, footerFiles,
      markdownModules: moduleStore.markdownModules,
      excludedKeys, hiddenSeparatorIndexes,
    });
    if (!hasContent) return;
    onSend(formData, fullPrompt, sendMeta);
    setFooterFiles([]); // 发送后清空附件
  }, [
    config, defaultText, selectedSkill, isAgentActive, aiImageActive,
    effectiveConfig, values, files, footerFiles, footerReports, onFooterReportsChange,
    footerFileUrls, onFooterFileUrlsChange, moduleStore.markdownModules,
    // isProcessing, disabled, parsingKeys, reportUrlParsingKeys, videoParsingKeys, validate, onSend, excludedKeys, hiddenSeparatorIndexes,
    isProcessing, loading, disabled, isSendBlocked, parsingKeys, reportUrlParsingKeys, videoParsingKeys, validate, onSend, excludedKeys, hiddenSeparatorIndexes,
  ]);

  /** 默认 empty 模式回车发送（对齐 V1 keyDownHandler config.length===0 分支）：
   *  Enter（无 Shift）→ preventDefault 阻换行 + 发送；Shift+Enter → 换行不发送；
   *  IME 合成态（isComposing / keyCode 229）→ return 放行，让输入法确认候选，不发送。 */
  const handleDefaultKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Enter') return;
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      if (e.shiftKey) return; // 换行
      e.preventDefault();
      void handleSend();
    },
    [handleSend],
  );

  /** 发送按钮是否可用（对齐 V1 isFormValid）：
   *  普通对话模式（config=[]）→ 有非空文本即 valid（V1 emptyChecker setIsFormValid(!isEmpty)）；
   *  表单模式 → computeFormValid：required 字段满足，且同一 exclusionGroup 只需填一个、
   *  defaultValue 视为已填（V1 validateRequiredFields 互斥语义）。
   *  仅用于发送按钮前置禁用态；handleSend 内仍有运行时拦截兜底。 */
  const isFormValid = useMemo(() => {
    if (parsingKeys.size > 0 || reportUrlParsingKeys.size > 0 || videoParsingKeys.size > 0) return false;
    if (config.length === 0) return defaultText.trim().length > 0;
    if (!hasRequiredMarkdownParseResult(config, moduleStore.markdownModules, excludedKeys)) return false;
    return computeFormValid(effectiveConfig, values, files, excludedKeys);
  }, [
    parsingKeys,
    reportUrlParsingKeys,
    videoParsingKeys,
    config,
    defaultText,
    moduleStore.markdownModules,
    effectiveConfig,
    values,
    files,
    excludedKeys,
  ]);

  /** 测量内嵌技能 tag（可蛇形换行）→ 算 textarea 的 text-indent(末行右端) + padding-top(多出行数)。
   *  tag 文字（技能名）变化或选中态切换都要重测；未选中归零。
   *  依赖必须带 config.length：从智能体态点技能切换时，selectedSkill 先 set（此帧 config 还 >0、
   *  走结构化分支，skillTagRef 尚未渲染 → effect 拿到 null ref 提前 return），下一帧消费端清掉
   *  agent 后 config 才塌到 0、ref 才出现。若只依赖 selectedSkill，这两帧间 selectedSkill 没变 →
   *  effect 不重跑 → 缩进永远 0 → 文字与 tag 重叠错位。 */
  const measureSkillTag = useCallback((): boolean => {
    const wrap = skillTagRef.current;
    const tagNode = wrap?.querySelector<HTMLElement>('.tag-node');
    if (!wrap || !tagNode) return false;
    // tag-node 为 inline 元素，getClientRects() 按行返回各行片段的边框盒（含左右 8px padding，
    // 即每行 chip 的视觉右端）。取最后一行右端作首行缩进基准，末行/首行 top 差算多出行下推高度。
    const lineRects = tagNode.getClientRects();
    if (lineRects.length === 0) return false;
    const wrapLeft = wrap.getBoundingClientRect().left;
    const firstRect = lineRects[0];
    const lastRect = lineRects[lineRects.length - 1];
    // textarea 文本起点（viewport）= 容器 border 内沿 + padding-left(16)。wrap left:8（绝对定位
    // 相对 border 内沿），故 border 内沿 = wrapLeft - 8，文本起点 = wrapLeft - 8 + 16 = wrapLeft + 8。
    // 缩进 = tag 末行右端 + 间距(6) - 文本起点 = lastRight - wrapLeft - 2。clamp 防负值。
    const width = Math.max(0, lastRect.right - wrapLeft - 2);
    setSkillTagWidth(width);
    // 多行下推量直接用「末行 top − 首行 top」实测（tag 行高 30px ≠ textarea 26px，不能用常量），
    // 让 textarea 首行垂直落到 tag 末行同高；单行时两 rect 同 top → 0。
    setSkillTagPadTop(Math.max(0, Math.round(lastRect.top - firstRect.top)));
    // 宽度 > 0 才算测到真实布局：tag 文字尚未排版/字体未加载/容器未布局时 rect 宽度≈0，
    // 返回 false 让调用方继续 rAF 重试，避免 text-indent 卡在 0 → 文本压住 tag。
    return width > 0;
  }, []);

  useLayoutEffect(() => {
    if (!selectedSkill && !aiImageActive) {
      setSkillTagWidth(0);
      setSkillTagPadTop(0);
      return;
    }
    let cancelled = false;
    let rafId = 0;
    // 确定性 rAF 重试：measureSkillTag 测到 0 宽度（tag 文字未排版／字体未加载／容器布局未稳／
    // 从 agent 态切换的过渡帧）时返回 false，逐帧重试到拿到真实宽度。这是 text-indent 卡在 0、
    // 文本压住 tag 的根因——wrap 宽度被 left:8/right:16 钉死，tag 内容/字体变化不改 wrap 尺寸，
    // 故挂在 wrap 上的 ResizeObserver 不会 fire，单测一次失败就永远不再纠正。rAF 重试覆盖所有
    // 时序竞态，不依赖任何观察器恰好触发。上限 30 帧（~0.5s）防极端情况死循环。
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      if (measureSkillTag() || ++tries >= 30) return;
      rafId = requestAnimationFrame(tick);
    };
    tick();
    // 响应式重测：容器/输入区宽度变化 → tag 重新换行（行数、末行右端都变），必须重测才能让
    // text-indent/padding-top 跟上。wrap 横跨 left:8~right:16，其宽度随容器变 → RO 可靠捕获。
    const wrap = skillTagRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') {
      return () => {
        cancelled = true;
        if (rafId) cancelAnimationFrame(rafId);
      };
    }
    const ro = new ResizeObserver(() => measureSkillTag());
    ro.observe(wrap);
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [selectedSkill, aiImageActive, config.length, measureSkillTag]);

  // 选中技能 / 进入 AI生图 后自动聚焦输入框（textarea 在 config=[] 时才渲染，从智能体切过来时需要等下一帧）
  useEffect(() => {
    if ((!selectedSkill && !aiImageActive) || config.length > 0) return;
    defaultTextareaRef.current?.focus();
  }, [selectedSkill, aiImageActive, config.length]);

  // 编辑器交互态：用户 mousedown 在根容器或浮层宿主（智能体下拉/技能面板/Popover 等 portal）
  // 内时为 true，落在外部时翻 false。靠这个 class 显示紫色选中态边框，不抢焦点。
  // 配合 CSS 里 :focus-within 兜底键盘聚焦场景。（rootRef 在上方 messageHost 处已声明）
  const [editorActive, setEditorActive] = useState(false);
  const [editorScrolling, setEditorScrolling] = useState(false);
  const editorScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEditorScroll = useCallback(() => {
    setEditorScrolling(true);
    if (editorScrollTimerRef.current !== null) {
      clearTimeout(editorScrollTimerRef.current);
    }
    editorScrollTimerRef.current = setTimeout(() => {
      setEditorScrolling(false);
      editorScrollTimerRef.current = null;
    }, 700);
  }, []);
  useEffect(() => () => {
    if (editorScrollTimerRef.current !== null) {
      clearTimeout(editorScrollTimerRef.current);
    }
  }, []);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const target = e.target as Node;
      const nextActive = root.contains(target) || Boolean(popupHost?.contains(target));
      setEditorActive(nextActive);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popupHost]);

  const [flowHovered, setFlowHovered] = useState(false);
  const [flowFocused, setFlowFocused] = useState(false);
  const flowVisible = editorActive || flowHovered || flowFocused;
  const [{ running: flowAnimationRunning, revision: flowAnimationRevision }, setFlowAnimation] = useState({
    running: false,
    revision: 0,
  });
  useLayoutEffect(() => {
    if (flowVisible) {
      setFlowAnimation((previous) => previous.running
        ? previous
        : { running: true, revision: previous.revision + 1 });
      return;
    }

    const timer = window.setTimeout(() => {
      setFlowAnimation((previous) => previous.running
        ? { ...previous, running: false }
        : previous);
    }, FLOW_VISIBILITY_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [flowVisible]);

  const handleSkillPanelOpenChange = useCallback((open: boolean) => {
    onSkillPanelOpenChange?.(open);
  }, [onSkillPanelOpenChange]);

  /** 默认 empty 模式 textarea autosize：内容变化时按 scrollHeight 撑高，
   *  先归零再读 scrollHeight 才能正确收缩。skillTagWidth 影响首行可用宽 →
   *  也要重算高度（首行变窄可能多挤一行出来）；skillTagPadTop 进 padding 盒同样要重算。 */
  useLayoutEffect(() => {
    const el = defaultTextareaRef.current;
    if (!el) return;
    // height='auto' 会把 textarea 瞬间坍缩成一行，下一行读 scrollHeight 强制同步布局时，
    // 滚动容器 .rte-editor 的内容高骤减 → 浏览器把它的 scrollTop clamp 到 ~0，且高度恢复后
    // 不会自动还原 → 内容满高、滚到中间编辑时每敲一键（含 IME 候选更新）视口都被甩走。
    // 故 autosize 前后保存/还原滚动位置——打字不该移动滚动条（贴底跟随由 pinBottomIfTyping
    // 按「变高前 atBottom」单独负责）。内容变短时赋值会被浏览器自动 clamp 到新的最大值，安全。
    const scroller = editorWrapperRef.current;
    const prevScrollTop = scroller?.scrollTop ?? 0;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    if (scroller && scroller.scrollTop !== prevScrollTop) scroller.scrollTop = prevScrollTop;
    // 强制 text-indent 重排：粘贴带换行的文本后、把光标移到换行处删除合并行时，textarea 的
    // text-indent 值仍在（computed 正确为 skillTagWidth），但浏览器不会自动按它重新排版首行
    // → 文字停在 x=0 压住内嵌技能 tag（正常逐字输入会触发重排故无此问题）。这里在内容变化后
    // 同步 toggle 一次 text-indent（0 → 实际值，中间读 offsetWidth 触发 reflow），逼浏览器
    // 按 indent 重新排版。useLayoutEffect 在绘制前执行，无闪烁。仅选中技能（有缩进）时需要。
    if (skillTagWidth > 0) {
      el.style.textIndent = '0px';
      void el.offsetWidth; // 触发 reflow，使下一行赋值被当作真实变化重新排版
      el.style.textIndent = `${skillTagWidth}px`;
    }
  }, [defaultText, skillTagWidth, skillTagPadTop]);

  // editorMinHeight / editorPadding 以 CSS 变量注入（V1 RichTextEditor/index.tsx parity）：
  // number → `${n}px`，string 原样透传；未传则不注入。.rte-editor 在 CSS 里读这两个变量。
  const containerStyle: React.CSSProperties = {
    width: '100%',
    background: background ?? (isDarkTheme ? 'rgba(10, 10, 10, 0.9)' : '#FFFFFF'),
    border,
    maxHeight,
    // minHeight 只在非折叠态应用（对齐 V1 index.tsx:1241 `minHeight && !collapsed`）：
    // 折叠时容器须收缩到「第一行 editor + footer」的自然高度，否则 minHeight 撑住容器、
    // editor 被 max-height 压到 46 → footer 下方留白（实测折叠后 footer 距容器底 47px）。
    ...(minHeight && !collapsed ? { minHeight } : {}),
    ...(editorMinHeight
      ? {
        ['--rte-editor-min-height']:
          typeof editorMinHeight === 'number' ? `${editorMinHeight}px` : editorMinHeight,
      }
      : {}),
    ...(editorPadding ? { ['--rte-editor-padding']: editorPadding } : {}),
  } as React.CSSProperties;
  const themeShellStyle: React.CSSProperties = width === undefined ? {} : { width };

  // editor 区在 .rich-text-editor flex column 里 flex:1 占满剩余高度，footer 自然贴底。
  //   - 非 collapsed：max-height = max(展开测量值, 折叠值)，overflow:auto，内容超长内部滚动
  //   - collapsed：max-height = 第一行可视高度，overflow:hidden
  // 两端都是像素值 → max-height transition 双向都能平滑动画（对齐 V1）。
  // 点击 editor 区域时调用 onCollapsedChange?.(false) 自动展开。
  // collapsedTotalHeight 传入时折叠 max-height 直接用该值（消费端约定的固定折叠高度），
  // 否则用内部 Range 测得的第一行可视高度。
  const effectiveCollapsedPx =
    typeof collapsedTotalHeight === 'number' ? collapsedTotalHeight : collapsedMaxHeightPx;
  const editorMaxHeightPx = collapsed
    ? effectiveCollapsedPx
    : Math.max(expandedMaxHeightPx, effectiveCollapsedPx);
  // transition 要让浏览器插值，必须在 max-height 改变「之前」就已 paint 在元素上。
  // 若在 collapsed 翻转的同一次 commit 里才把 transition 从 none→有，浏览器同帧拿到新
  // max-height、无旧值起点 → 直接跳变（实测收起首帧即 165→46，无动画）。故 transition
  // 常驻，max-height 任何变化都平滑插值。
  //
  // 例外（disableTransition=true 时设 none）：agent 切换瞬间 expanded 被放开成 9999、
  // RO 再收敛到真实值，这一来一回若插值会产生「先猛涨再回落」的可见跳动；agent 切换不是
  // 用户主动的展开/收起手势，不应有动画。fingerprint 变化的那一拍～收敛完成期间禁 transition。
  const editorWrapperStyle: React.CSSProperties = {
    // editor 显式 inline max-height（= editorMaxHeightPx，展开态 = clamp 到「父可用高 availInParent」
    // 的 expandedMaxHeightPx）：**不能**只靠「flex:1 + 父 max-height」让 flex 自动收缩——在
    // max-height（非固定 height）的 column flex 容器里，触顶后 footer/filesList/editor 会按
    // flex-shrink 比例一起缩，而非单独把 editor 夹出滚动条 → 表现为「框一直变高、不停在上限」
    // （用户实测）。故必须给 editor 一个确定的像素 cap。
    maxHeight: editorMaxHeightPx,
    // collapsed 或动画窗口内都置 0：.rte-editor 有 CSS min-height:58px，展开瞬间 collapsed→false
    // 若 minHeight 立即恢复 undefined → CSS 58px 生效 → 元素从收起态 46 瞬跳到 58（顿挫），且被
    // 58 钉住直到 max-height 动画爬过 58 才可见长高（前 ~50ms 视觉静止「顿一下」）。动画期间保持 0，
    // 元素跟随 max-height 从 46 连续长到目标，无卡顿；窗口结束恢复（此时高度已 ≥58，CSS 58 无影响）。
    // minHeight 三态：
    //   - collapsed（含折叠动画中）：0。折叠靠 max-height 上限把元素从 122 压到 46，min-height 必须
    //     让路（设 46 会让元素瞬间锁死在 46→折叠无动画）。
    //   - 展开动画中（!collapsed && collapseAnimating）：= editorMaxHeightPx（展开目标，如 122）。
    //     关键修复：空框/短内容的展开高度来自 flex 撑满父容器、而非内容本身。max-height 只是「上限」，
    //     动画期间上限从 46 涨到 122 时，内容只有 ~46 的空框根本撑不到上限 → 元素卡在 ~46 一动不动，
    //     直到动画结束 collapseAnimating=false、flex 撑满才一帧 snap 到 122（用户实测的「二段式：先几乎
    //     不动，末尾猛跳 180」）。把 min-height 一并设为目标并纳入 transition，元素被 min-height 平滑
    //     顶起 46→122，动画结束释放为 undefined 时高度已是 122、flex 无缝接管，无 snap。
    //   - 稳定展开态：undefined（交给 flex 撑满，见 maxHeight 注释）。
    minHeight: collapsed ? 0 : collapseAnimating ? editorMaxHeightPx : undefined,
    // collapsed 或动画窗口内 hidden：折叠区固定高、展开动画中间帧 max-height < scrollHeight 若 auto 会闪滚动条。
    // 稳定展开态固定 auto：高度由 flex 决定（见上 maxHeight 注释），内容放得下时 scrollHeight==clientHeight、
    // auto 不显滚动条也不回弹；内容超父上限时本元素自动出滚动条可内部滚。不再依赖 bodyOverflowing
    // （那套「测内容是否溢出再切 auto/hidden」是 inline max-height 接管高度时代的产物，flex 接管后多余）。
    overflow: collapsed || collapseAnimating ? 'hidden' : 'auto',
    // min-height 必须与 max-height 同步过渡：展开动画用 min-height 把空框顶到目标高（见 minHeight 注释），
    // 不纳入 transition 则 min-height 0→122 会瞬间生效、首帧 snap，失去动画。
    transition: disableTransition
      ? 'none'
      : `max-height ${collapseTransitionMs}ms cubic-bezier(0.4, 0, 0.2, 1), min-height ${collapseTransitionMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    willChange: 'max-height, min-height',
  };

  const handleEditorAreaClick = useCallback(() => {
    // toggleOnBlankClick=false 时禁用「点空白展开」（消费端在非贴底场景下关闭）
    if (collapsed && toggleOnBlankClick) onCollapsedChange?.(false);
    // 普通对话模式：textarea 仅一行高、容器很高，点击下方空白要把光标落到 textarea。
    // 智能体表单态：defaultTextareaRef 为 null，不抢焦点——下拉/上传等 chip 自己处理 click，
    // 这里再 querySelector 抢首个输入框会把焦点从被点中的下拉拽走。选中态边框走 editorActive
    // 的 .rte-editor-selected class 即可，不依赖此处。
    defaultTextareaRef.current?.focus();
  }, [collapsed, toggleOnBlankClick, onCollapsedChange]);

  // 输入跟随：表单态往下加内容（multi-variable 冒新空槽、textarea/variable chip 换行变高、
  // 粘贴 URL）超过 editor 可视高后，最新输入位落在滚动区下方——用户「超出了就该自动定在底部，
  // 不该手动滑」。
  // 关键时机：内容增高不在 onInput 当帧发生——VariableNode 的 multiline 升级走 onChange 后的
  // useLayoutEffect→measure→setState→重渲染，textarea autosize 同理。onInput 里单帧 rAF
  // scrollTop=scrollHeight 滚的是**旧高度**的底，chip 随后才长高 → 新内容又跑到下方（用户：还要手滑）。
  // 故 onInput 只打一个「用户刚在本编辑器输入」的时间戳，真正贴底交给 editor 的 ResizeObserver
  // （内容 DOM 真变高后才触发，见下方 measureHeights 的 RO），在那里按时间戳判定是否贴底。
  const lastTypingAtRef = useRef<number>(0);
  // 输入「变高前」用户是否贴着底部（stick-to-bottom 语义的关键前提）。
  // 只有原本在底部才在内容变高后继续贴底；滚到中间编辑时不抢滚动位置。
  const wasAtBottomRef = useRef<boolean>(true);
  const handleEditorInput = useCallback(() => {
    const el = editorWrapperRef.current;
    const active = document.activeElement as HTMLElement | null;
    if (!el || !active || !el.contains(active)) return;
    lastTypingAtRef.current =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    // 当帧 DOM 尚未因本次输入重排（textarea autosize / chip multiline 都在随后的
    // useLayoutEffect 才撑高），故此刻 scrollHeight 仍是旧值 → 能准确反映「变高前」的滚动位置。
    // 容差 4px：允许光标在末行、视觉已贴底的微小误差。
    wasAtBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= 4;
  }, []);

  // 测量 editor 区真实高度：展开值 = scrollHeight；折叠值 = 第一行可视高度。
  // 两者都存成像素 state，供 editorWrapperStyle 在两端之间过渡（功能对齐 V1，实现不照搬）。
  //
  // V1 用「9999 兜底 + rAF 锁顶 + 150ms 二次测量 + 滚轮拦截」一堆命令式补丁来应付
  // 「内容异步稳定后高度变化测不到」，V2 改用 ResizeObserver 实时跟随，更干净也更可靠：
  //   - 切换 agent / 节点异步渲染（视频 chip 图片、字体 swap 重排、multi-variable 追加空行）
  //     都会改变 editor 内容高度 → RO 回调重测 → max-height 实时跟进，不会钉在中间态。
  //   - 死锁规避：max-height 一旦被钉得比 scrollHeight 小，editor 被 clamp、border-box 不再
  //     随内容增长，RO 就测不到「内容还在变高」→ 永远停在偏小值（当前 bug）。故 agent 切换
  //     （configFingerprint 变化）时先把 expanded 放开成安全大值，让 editor 不 clamp、RO 能
  //     观察到真实高度，再由 RO 回调收敛到精确 scrollHeight。展开态 max-height≥scrollHeight
  //     恒不裁切 → border-box 跟随内容 → RO 持续有效，收敛后 prev===next 守卫止住、不回环。
  const measureHeights = useCallback(() => {
    const el = editorWrapperRef.current;
    if (!el) return;

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // 折叠/展开动画窗口内：整个测量函数直接跳过。折叠目标(collapsedMaxHeightPx)与展开目标
    // (expandedMaxHeightPx)在动画开始前已测定，动画只是在这两个已知像素值间做 max-height CSS
    // 插值，期间无需重测。而 max-height 每帧插值会让 editor border-box 每帧变 → 下面挂的两个
    // ResizeObserver 每帧 fire → 每帧跑 getBoundingClientRect + Range.getClientRects +
    // getComputedStyle 的强制同步布局，造成逐帧布局风暴（折叠卡顿根因）。动画期间跳过即可消除，
    // 窗口结束后由 collapseAnimating 复位 effect 补一次收敛测量。
    if (now < collapseAnimUntilRef.current) return;

    // 折叠态：绝不测/改 expanded，也绝不摘 max-height——折叠动画正在用 inline max-height 插值，
    // 任何对 max-height 的临时改写都会让浏览器丢失动画起点 → 瞬跳（实测 RO 在动画中触发摘 cap
    // 会把收起打回 131→46 瞬跳）。折叠只需 collapsed 第一行高度，下面单独测。
    if (!collapsed) {
      // 展开高度：editor 是 flex:1 子项，被偏小 max-height clamp 时 client/scrollHeight 都读不到
      // 「本应 flex 撑满容器剩余空间」的高度（死锁：测小→钉小→还是小）。普通对话 config=[] 仅一行
      // textarea 内容 58 但容器 minHeight 150，首屏卡 58 → footer 上方留白。
      // 破法：测量瞬间临时摘掉 inline max-height 强制同步 reflow，读 offsetHeight（= flex 撑满后
      // 真实高 = max(内容高, 容器剩余空间)），再立即恢复。同步执行不进 paint、无闪烁。
      // 动画窗口已在函数开头统一守卫跳过，此处直接测量。
      {
        const prevMaxH = el.style.maxHeight;
        el.style.maxHeight = 'none';
        // 摘 cap 期间（editor 撑开、父被自身 max-height:350 clamp 到真实上限）一次性读全所有量。
        // 关键：父可用高度必须在**恢复 prevMaxH 之前**读——恢复后 editor 被旧的偏小 cap 夹回去、
        // 父也跟着缩小，会量出偏小的可用高 → expanded 钉小 → 测小钉小死锁（框停在中途撑不到 350）。
        const flexFilled = el.offsetHeight; // flex 撑满父剩余空间后的高
        const contentH = el.scrollHeight;   // editor 内容自然全高（可能远超父可用空间）
        // 父容器（.rich-text-editor，flex column + max-height + overflow:hidden）给 editor 的
        // 可用高度上限 = 父 clientHeight（已被 max-height:350 clamp）− editor 之外的兄弟
        // （FooterFilesList + footer）− 父 padding。把 expanded clamp 到此值，内容超高时 editor
        // maxHeight = 可用高 < scrollHeight → bodyOverflowing → overflow:auto → 内部可滚、不被裁。
        const parent = el.parentElement;
        let availInParent = Infinity;
        if (parent) {
          let siblingsH = 0;
          for (const sib of Array.from(parent.children)) {
            if (sib !== el && !(sib as HTMLElement).classList.contains('rte-scrollbar-overlay')) {
              siblingsH += (sib as HTMLElement).offsetHeight;
            }
          }
          const cs = getComputedStyle(parent);
          const padV = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
          availInParent = Math.max(0, parent.clientHeight - siblingsH - padV);
        }
        el.style.maxHeight = prevMaxH;
        // expanded 仍要测：它是折叠↔展开动画的「展开目标高度」（editorMaxHeightPx 在 collapsed/
        // 动画窗口消费它做像素插值）。内容高与「撑满父剩余空间」取大（保留普通对话首屏撑满、消除
        // footer 上方留白），再 clamp 到父可用高上限。稳定展开态的滚动已交给 CSS flex+overflow:auto
        // 处理（见 editorWrapperStyle），不再在此测 bodyOverflowing。
        const wanted = Math.max(Math.ceil(contentH || 0), Math.ceil(flexFilled || 0));
        const nextExpanded = Math.min(wanted, Math.ceil(availInParent));
        if (nextExpanded > 0) {
          setExpandedMaxHeightPx((prev) => (prev === nextExpanded ? prev : nextExpanded));
        }
      }
    }

    // 折叠高度：第一行所有 client rect 的底部相对 editor 顶部的距离（含内联 chip/下拉等高元素），
    // 避免写死 58px 截断第一行（第一行可能比 58 高）。
    // 注意：实际承载 inline chip 流的是 .rte-config-fade 内层（agent 切换淡入 wrapper），
    // 若对外层 .rte-editor selectNodeContents，Range 只返回该 block wrapper 的整块 rect（一行）→
    // 第一行高度被算成整个内容高度 → 折叠失效。故优先选 fade 内层的内容来测真实首行。
    try {
      const editorRect = el.getBoundingClientRect();
      const inlineHost = el.querySelector<HTMLElement>('.rte-config-fade') ?? el;
      const range = document.createRange();
      range.selectNodeContents(inlineHost);
      const rects = Array.from(range.getClientRects()).filter((r) => r.height > 0);
      if (rects.length > 0) {
        const firstTop = Math.min(...rects.map((r) => r.top));
        // 第一行归组——单轮「半行高 top 窗口」：把 top 落在 [firstTop, firstTop + lineBox*0.5) 内的
        // rect 都算第一行，取其最大 bottom。
        //
        // 走过两版弯路（都因不同场景翻车，现用半行高窗口同时满足）：
        // ① 固定整行窗口 `top < firstTop + lineBox(34)`：密排时行 1 底≈46、行 2 顶≈49，34px 窗口
        //    越过 3px 缝隙把行 2 拉进来 → 折叠仍两行。
        // ② 迭代收敛（top<当前 bottom 就吸收扩张、循环）：遇到「跨行桥接 rect」会链式串联——上传图片后
        //    第一行底 45.8 处有个紧贴行边界的窄 rect（top≈45.8 bottom≈57.8，required 星号/分隔），
        //    把 bottom 推到 57.8 → 又越过第二行 64px 高图片盒的 top(51.8) 把整盒吞入 → 串到 81.6（两行）。
        //    迭代「无限串联」对紧贴行界的高元素不鲁棒。
        //
        // 半行高窗口（17px）取舍：实测同一视觉行内元素 top 跨度仅 6~8px（vertical-align 偏移），< 17 纳入；
        // 第二行 top 距 firstTop ≈ 一整行（30~34px），> 17 被排除——既不漏首行高 chip，也不串到第二行。
        // 关键：只看 rect 的 **top 是否落在首行窗口**（单轮、不迭代），高元素只要 top 在首行就用其完整
        // bottom（首行单图 chip 完整露出），但其大 bottom 不会反过来把别的行拉进来（不串联）。
        const lineHeightPx = parseFloat(getComputedStyle(el).lineHeight) || 34;
        const lineBox = Math.max(lineHeightPx, 1);
        const firstLineWindow = firstTop + lineBox * 0.5;
        let firstLineBottom = 0;
        for (const r of rects) {
          if (r.top < firstLineWindow) {
            firstLineBottom = Math.max(firstLineBottom, r.bottom);
          }
        }
        const measured = firstLineBottom > 0 ? Math.ceil(firstLineBottom - editorRect.top) : 0;
        // 下限兜底：折叠态至少要露出「padding-top + 一个行盒」，否则万一 Range 测出异常小值
        // （图片 chip 未加载完高度为 0、空白 rect 等边界）会把第一行整个切没（用户实测：连续对话页
        // 单图未换行时折叠成空白）。以单行自然高为地板，measured 比它还小则取地板，绝不坍成空白。
        const csEl = getComputedStyle(el);
        const padTop = parseFloat(csEl.paddingTop) || 0;
        const minFirstLine = Math.ceil(padTop + lineBox);
        const next = Math.max(measured, minFirstLine);
        if (next > 0) {
          setCollapsedMaxHeightPx((prev) => (prev === next ? prev : next));
        }
      }
    } catch {
      // Range API 在极端环境下可能抛错，忽略并保留上次测量值。
    }

    // collapsed 必须进依赖：否则 RO 回调里读到的是 stale collapsed=false，折叠动画期间
    // 仍会跑上面的「摘 max-height 测 expanded」分支 → 浏览器丢失 max-height 动画起点 → 瞬跳。
  }, [collapsed]);

  // agent 切换检测（render 期间同步）：configFingerprint 变化时立即把 expanded 放开成安全大值，
  // 破除「max-height 钉死 < scrollHeight → editor clamp → RO 测不到内容增长」的死锁。
  // RO 回调随后会把它收敛到精确 scrollHeight。仅 key+type 指纹，模块子节点不计入（避免解析展开误触发）。
  const configFingerprint = useMemo(
    () =>
      effectiveConfig
        .filter((n) => n.key && n.type !== 'report-module' && n.type !== 'markdown-module')
        .map((n) => `${n.type}:${n.key}`)
        .join('|'),
    [effectiveConfig],
  );
  const prevFingerprintRef = useRef(configFingerprint);
  if (prevFingerprintRef.current !== configFingerprint) {
    prevFingerprintRef.current = configFingerprint;
    if (!collapsed) {
      setExpandedMaxHeightPx(LARGE_SAFE_HEIGHT);
      setDisableTransition(true);
    }
  }
  // agent 切换后短窗口结束恢复 transition（让随后用户主动 collapsed 切换重新平滑）。
  // 依赖 configFingerprint：每次 agent 切换重启窗口。collapseTransitionMs 足够覆盖 RO 收敛。
  useEffect(() => {
    if (!disableTransition) return;
    const t = setTimeout(() => setDisableTransition(false), collapseTransitionMs);
    return () => clearTimeout(t);
  }, [disableTransition, configFingerprint, collapseTransitionMs]);

  // 首测 + ResizeObserver 持续跟随内容高度变化（取代 V1 的 rAF/150ms 二次测量）。
  // 依赖内容相关量变化时重建（含 fingerprint：agent 切换放开后立即重测收敛）。
  //
  // 关键：除了观察外层 wrapper（el），还必须观察**内层未被裁切的内容**（默认模式的 textarea /
  // 配置模式的 .rte-config-fade）。死锁根因：wrapper 被 inline `max-height + overflow:hidden`
  // 钉死后，它自身 border-box 恒定（实测内容长到 1384 而 wrapper offsetHeight 仍 92）→ RO 永不
  // fire → measureHeights 不重跑 → max-height 冻在偏小值 → 内容溢出却不可滚（用户：超长文本无法滚动）。
  // 内层元素不受 wrapper 裁切（textarea autosize 到 scrollHeight、fade 随 chip 自然撑高），其 box
  // 高度变化能稳定触发 RO，无论改动来自 defaultText 还是某个 node value（这些都不在下面 deps 里）。
  useLayoutEffect(() => {
    const el = editorWrapperRef.current;
    if (!el) return;
    measureHeights();
    if (typeof ResizeObserver === 'undefined') return;
    // 贴底：editor 被 maxHeight 夹住后其 border-box 不再随内容长高 → 观察 editor 自身的 RO 测不到
    // 「内容还在变高」（同 measureHeights 注释里的死锁）。故贴底改为观察**内容层** .rte-config-fade
    // （不受 editor cap 限制、chip multiline / textarea autosize / 新空槽都让它持续长高）。
    const pinBottomIfTyping = () => {
      const el2 = editorWrapperRef.current;
      if (!el2) return;
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      // 仅当「用户刚在本编辑器输入」（600ms 窗口，排除 agent 切换/异步渲染等非输入高度变化抢滚动位置）
      // + 焦点仍在 editor 内 + 内容确实溢出 + 变高前用户原本就贴着底部时才贴底。
      // 最后一个前提是关键：滚到中间编辑某行时（wasAtBottom=false）不把用户拽回底部。
      if (
        now - lastTypingAtRef.current < 600 &&
        wasAtBottomRef.current &&
        el2.contains(document.activeElement) &&
        el2.scrollHeight > el2.clientHeight
      ) {
        el2.scrollTop = el2.scrollHeight;
      }
    };
    const ro = new ResizeObserver(() => measureHeights());
    ro.observe(el);
    // 观察内容层（默认输入框 textarea / 结构化 config-fade）——editor 被 maxHeight 钳住后
    // border-box 冻结，观察 editor 自身测不到内容增长；内容变高先让 cap 收敛到新可用高，再贴底。
    const contentEl = el.querySelector<HTMLElement>('.rte-default-textarea, .rte-config-fade');
    const contentRo = contentEl
      ? new ResizeObserver(() => {
        measureHeights();
        pinBottomIfTyping();
      })
      : null;
    if (contentEl && contentRo) contentRo.observe(contentEl);
    return () => {
      ro.disconnect();
      contentRo?.disconnect();
    };
  }, [measureHeights, effectiveConfig, files, footerFiles.length, footerReports.length, footerFileUrls.length, reportNames, collapsed, configFingerprint, defaultText]);

  // 展开态真实高度变化 → 通知消费端（算对话列表底部留白）。仅非折叠态、且高度为已收敛的真实值
  // （排除 agent 切换瞬间放开的 LARGE_SAFE_HEIGHT 临时值，避免回传一个虚高的过渡值）时触发。
  const prevExpandedNotifyRef = useRef<number>(-1);
  useEffect(() => {
    if (collapsed) return;
    if (expandedMaxHeightPx <= 0 || expandedMaxHeightPx >= LARGE_SAFE_HEIGHT) return;
    if (prevExpandedNotifyRef.current === expandedMaxHeightPx) return;
    prevExpandedNotifyRef.current = expandedMaxHeightPx;
    onExpandedHeightChange?.(expandedMaxHeightPx);
  }, [expandedMaxHeightPx, collapsed, onExpandedHeightChange]);

  /** 粘贴文件 → 路由到 footerFiles（V1 兼容）。
   *  - 含 tag 节点（已选 agent）时拒绝
   *  - 按扩展名白名单过滤
   *  - 50MB 总量校验（含已有 footerFiles）
   *  - 文字粘贴（clipboardData.files 为空）不劫持 */
  const PASTE_ALLOWED_EXTS = useMemo(
    () =>
      new Set([
        'pdf', 'xla', 'xlb', 'xlc', 'xlm', 'xls', 'xlsx', 'xlt', 'xlw', 'csv', 'tsv', 'iif',
        'doc', 'docx', 'dot', 'odt', 'rtf', 'pot', 'ppa', 'pps', 'ppt', 'pptx', 'pwz', 'wiz',
        'asm', 'bat', 'c', 'cc', 'conf', 'cpp', 'css', 'cxx', 'def', 'dic', 'eml', 'h', 'hh',
        'htm', 'html', 'ics', 'ifb', 'in', 'js', 'json', 'ksh', 'png', 'jpeg', 'jpg', 'gif',
        'webp', 'md', 'php', 'pkl', 'rb', 'tar', 'tex', 'ts', 'txt', 'xml',
        // 视频：无单文件上限，仅受 50MB 总量约束（同其它文件）
        'mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', 'xmind', 'zip',
        // 扩充图片格式（与 footerFileLimits 的 ALLOWED_FOOTER_EXTENSIONS 保持一致）
        'bmp', 'dib', 'tiff', 'tif', 'ico', 'icns', 'sgi', 'avif', 'heif', 'pic',
        'j2c', 'j2k', 'jp2', 'jpc', 'jpf', 'jpx',
        // 思维导图
        'mm',
      ]),
    [],
  );
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      const hasAgent = config.some((n) => n.type === 'tag');
      // 报告链接粘贴：仅普通对话（无 agent）时劫持。从粘贴文本里切出报告 URL 交给消费端
      // 解析（进 footer 报告 chip），链接前后的文案回落到输入框光标处——支持「链接；问题」
      // 这类整段粘贴，不再把后续文案一起吞进 URL。非报告链接则照常落进输入框。
      const pastedText = e.clipboardData?.getData?.('text/plain') ?? '';
      const reportPaste = onReportUrlPaste && !hasAgent ? extractReportUrls(pastedText) : null;
      if (reportPaste) {
        e.preventDefault();
        reportPaste.urls.forEach((url) => onReportUrlPaste?.(url));
        if (reportPaste.rest) {
          const ta = defaultTextareaRef.current;
          if (ta) {
            const src = ta.value;
            const start = ta.selectionStart ?? src.length;
            const end = ta.selectionEnd ?? src.length;
            setDefaultText(src.slice(0, start) + reportPaste.rest + src.slice(end));
            const caret = start + reportPaste.rest.length;
            requestAnimationFrame(() => {
              try {
                ta.setSelectionRange(caret, caret);
                ta.focus();
              } catch {
                /* 光标恢复失败不影响文案已插入 */
              }
            });
          } else {
            setDefaultText((prev) => (prev ? `${prev}${reportPaste.rest}` : reportPaste.rest));
          }
        }
        return;
      }

      const pastedFiles = e.clipboardData?.files;
      if (!pastedFiles || pastedFiles.length === 0) return;
      e.preventDefault();
      if (hasAgent) return;

      const validFiles: File[] = [];
      let hasInvalid = false;
      for (const f of Array.from(pastedFiles)) {
        const ext = (f.name || '').split('.').pop()?.toLowerCase() || '';
        if (PASTE_ALLOWED_EXTS.has(ext)) validFiles.push(f);
        else hasInvalid = true;
      }
      if (hasInvalid) messageRef.current?.error('粘贴文件中含不支持的类型，已自动过滤');
      if (validFiles.length === 0) return;

      const MAX_TOTAL_BYTES = 50 * 1024 * 1024;
      const currentTotal = footerFiles.reduce((s, f) => s + f.size, 0);
      const accepted: File[] = [];
      let runningTotal = currentTotal;
      for (const f of validFiles) {
        if (runningTotal + f.size > MAX_TOTAL_BYTES) {
          messageRef.current?.error('文件总大小不能超过 50MB');
          break;
        }
        accepted.push(f);
        runningTotal += f.size;
      }
      if (accepted.length > 0) {
        setFooterFiles((prev) => [...accepted, ...prev]);
      }
    },
    [PASTE_ALLOWED_EXTS, config, footerFiles, onReportUrlPaste],
  );

  return (
    <ConfigProvider getPopupContainer={resolvePopupContainer} theme={antdThemeConfig}>
      {/* component={false} 不渲染包裹 div，只提供 message/notification/Modal 的 context。
          message holder 经 getContainer 渲到 messageHost（高 z-index 的 fixed 兄弟节点），
          绕开 Qiankun 沙箱代理 document.body 导致 toast 不弹的问题，同时保证 toast 浮在业务
          Modal 之上（popupHost 停在编辑器层级会被 Modal 遮挡，故 message 单独用 messageHost）。 */}
      <App component={false} message={{ getContainer: () => messageHost ?? popupHost ?? document.body }}>
        <MessageBridge messageRef={messageRef} />
        <PopupHostContext.Provider value={resolvePopupContainer()}>
          {/* render 期求值即可：首渲染时消费端 ref 可能未赋值而得 null，但 popupHost 挂载后的
            setState 重渲染会再次求值拿到真实节点；教程弹层由用户点击打开，届时必然已就绪。 */}
          <FullscreenOverlayHostContext.Provider value={getFullscreenOverlayContainer?.() ?? null}>
            <div
              className={`rte-theme-shell${editorActive ? ' rte-theme-shell--selected' : ''}${flowVisible ? ' rte-theme-shell--flow-visible' : ''}${flowAnimationRunning ? ' rte-theme-shell--flow-running' : ''}`}
              data-theme={editorTheme}
              style={themeShellStyle}
              onMouseEnter={() => setFlowHovered(true)}
              onMouseLeave={() => setFlowHovered(false)}
              onFocusCapture={() => setFlowFocused(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setFlowFocused(false);
                }
              }}
            >
              <div className="rte-flow-animation">
                <div className={`rte-flow-lights rte-flow-lights--${editorTheme}`} aria-hidden="true">
                  <svg key={flowAnimationRevision} className="rte-flow-border" fill="none" focusable="false">
                    {isDarkTheme ? (
                      <FlowBorderBands />
                    ) : (
                      <LightFlowBorderBands />
                    )}
                  </svg>
                </div>
                {isDarkTheme && (
                  <div className="rte-flow-edges" aria-hidden="true">
                    <svg key={flowAnimationRevision} className="rte-flow-border" fill="none" focusable="false">
                      <FlowEdgeBand variant="purple" />
                      <FlowEdgeBand variant="blue" />
                    </svg>
                  </div>
                )}
              </div>
              <div
                ref={rootRef}
                className={`rich-text-editor ${fullscreenRichtextKey ? 'rte-richtext-container-expanded' : ''} ${editorActive ? 'rte-editor-selected' : ''} ${className ?? ''}`}
                data-component="structured-editor"
                data-theme={editorTheme}
                style={containerStyle}
                onPaste={handlePaste}
              >
                {/* footer 上传的文件 chip 行：编辑区上方独立整行（对齐 V1，不在 footer 内）。
          只要有文件就显示（粘贴也能产生文件），不受 showFooterUpload 控制——该 flag 只管上传按钮。 */}
                <FooterFilesList
                  files={footerFiles}
                  onChange={setFooterFiles}
                  uploadProgress={footerFileUploadProgress}
                  reports={footerReports}
                  onReportRemove={(id) => onFooterReportsChange?.(footerReports.filter((r) => r.id !== id))}
                  fileUrls={footerFileUrls}
                  onFileUrlRemove={(id) => onFooterFileUrlsChange?.(footerFileUrls.filter((f) => f.id !== id))}
                />
                {config.length === 0 ? (
                  <div
                    ref={editorWrapperRef}
                    className={`rte-editor rte-editor-default${selectedSkill || aiImageActive ? ' rte-editor-default-with-skill' : ''}${editorScrolling ? ' rte-editor-scrolling' : ''}`}
                    style={editorWrapperStyle}
                    onClick={handleEditorAreaClick}
                    onInput={handleEditorInput}
                    onScroll={handleEditorScroll}
                  >
                    {/* 选中技能时：技能 tag 绝对定位在左上角（复用智能体 TagNode 的紫渐变样式），
              textarea 首行用 text-indent 让开 tag 宽度、换行后回到左边缘（flex 并排做不到）。
              普通对话上传文件能力不变（config 仍为空、hasAgent=false）。 */}
                    {(selectedSkill || aiImageActive) && (
                      <span ref={skillTagRef} className="rte-skill-inline-tag-wrap" contentEditable={false}>
                        {selectedSkill ? (
                          <SkillTag title={selectedSkill.title} onClose={() => handleSkillSelect(null)} />
                        ) : (
                          <SkillTag title="AI生图" exitTip="点击退出 AI 生图" onClose={() => { setAiImageActive(false); onAIImageExit?.(); }} />
                        )}
                      </span>
                    )}
                    {/* 受控原生 textarea：替代原 contentEditable，规避浏览器自动塞 <br>，
              光标由浏览器原生维护（不会跳到开头），autosize 随内容撑高 */}
                    <textarea
                      ref={defaultTextareaRef}
                      className="rte-default-textarea"
                      value={defaultText}
                      onChange={(e) => setDefaultText(e.target.value)}
                      onKeyDown={handleDefaultKeyDown}
                      placeholder={aiImageActive ? '请描述你想要的图片' : (selectedSkill?.usageInstruction || placeholder || '')}
                      disabled={disabled}
                      rows={1}
                      style={{
                        textIndent: skillTagWidth ? `${skillTagWidth}px` : undefined,
                        paddingTop: skillTagPadTop ? `${skillTagPadTop}px` : undefined,
                      }}
                    />
                  </div>
                ) : (
                  <div
                    ref={editorWrapperRef}
                    className={`rte-editor${fullscreenRichtextKey ? ' rte-richtext-fullscreen-active' : ''}${editorScrolling ? ' rte-editor-scrolling' : ''}`}
                    style={editorWrapperStyle}
                    onClick={handleEditorAreaClick}
                    onScroll={handleEditorScroll}
                    onInput={handleEditorInput}
                  >
                    {/* agent 切换时配置整体淡入（key=configFingerprint，仅 agent 切换变化，输入时不变 →
              不破坏节点 memo 隔离）。切换走 disableTransition 路径高度瞬到位，内容靠这层淡入软化，
              避免新配置硬切冒出。包一层 block div 不影响内部 chip 的 inline 流（line-height/
              white-space 继承自 .rte-editor）。editor 滚动容器/ref 在外层保持稳定。 */}
          <div className="rte-config-fade" key={configFingerprint}>
          {effectiveConfig.map((node, idx) => {
            // 条件渲染：依赖节点（如 select）未命中条件时直接跳过（separator 等无 key 节点也按索引隐藏）
            if (conditionalHiddenIndexes.has(idx)) return null;
            // V1 parity: 被互斥隐藏的节点跳过渲染（不是 disabled），separator 也按需隐藏
            if (node.key && excludedKeys.has(node.key)) return null;
            if (node.exclusionGroup === 'separator' && hiddenSeparatorIndexes.has(idx)) return null;
            // richtext 全屏态：仅渲染该 richtext 节点，其余全部隐藏（对齐 V1 覆盖式展开）
            if (fullscreenRichtextKey && node.key !== fullscreenRichtextKey) return null;
            const valueKey =
              (node.type === 'report-module' || node.type === 'markdown-module') && node.key
                ? `${node.key}__module_${node.moduleIndex ?? 0}`
                : node.key;
            return (
              <NodeRenderer
                key={valueKey ?? `text-${idx}`}
                node={node}
                value={valueKey ? values[valueKey] : undefined}
                files={files}
                onChange={onChangeValue}
                onFileChange={onChangeFile}
                thumbnails={thumbnails}
                onThumbnailChange={onThumbnailChange}
                videoUrlParse={videoUrlParse}
                videoUpload={videoUpload}
                mediaUpload={mediaUpload}
                onVideoParsingChange={onVideoParsingChange}
                reportListFetch={reportListFetch}
                shopListFetch={shopListFetch}
                onReportGoGenerate={onReportGoGenerate}
                onViewReport={onViewReport}
                hideCurrentSessionReport={hideCurrentSessionReport}
                reportNames={reportNames}
                onClearReport={handleClearReport}
                onResolveMultiName={onResolveMultiName}
                onAnalyzeMultiReport={onAnalyzeMultiReport}
                onShiftReportNames={handleShiftReportNames}
                onAgentExit={onAgentExit}
                hideAgentClose={hideButtons === 'hideTagClose'}
                onReportUrlParse={onReportUrlParse}
                onMarkdownParse={onMarkdownParse}
                onMarkdownNameParse={onMarkdownNameParse}
                onRichtextExpandChange={onRichtextExpandChange}
                disabled={disabled}
                error={valueKey ? errors[valueKey] : undefined}
                index={idx}
                nodeDefaults={nodeDefaults}
              />
            );
          })}
          </div>
        </div>
      )}
      <ScrollbarOverlay
        scrollRef={editorWrapperRef}
        hostRef={rootRef}
        clipRef={editorWrapperRef}
      />
      <Footer
          isProcessing={isProcessing}
          loading={loading}
          disabled={disabled}
          sendDisabled={isSendBlocked || !isFormValid}
          showFooterUpload={showFooterUpload}
          footerFiles={footerFiles}
          onFooterFilesChange={setFooterFiles}
          onAssetLibraryClick={onAssetLibraryClick}
          agentOptions={agentOptions}
          onAgentSelect={handleAgentSelect}
          hideAgentButton={hideButtons === 'hideAgent'}
          currentAgent={currentAgent}
          selectedSkill={selectedSkill}
          onSkillSelect={handleSkillSelect}
          onSkillCardClick={onSkillCardClick}
          onSkillAction={onSkillAction}
          skillListFetch={skillListFetch}
          onSkillHeaderAction={onSkillHeaderAction}
          showCreateSkill={showCreateSkill}
          createDisabled={createDisabled}
          skillPanelOpen={skillPanelOpen}
          onSkillPanelOpenChange={handleSkillPanelOpenChange}
          skillPanelOpenDelay={skillPanelOpenDelay}
          skillPanelHeight={skillPanelHeight}
          onAIImageClick={handleAIImageClick}
          aiImageSelected={aiImageActive}
          onAgentExit={handleAgentExit}
          onAIImageExit={handleAIImageExit}
          onSend={handleSend}
          onPause={onPause}
          canPause={externalCanPause ?? !isAgentActive}
          padding={footerPadding}
        />
        </div>
        </div>
        {/* 浮层宿主：与 .rich-text-editor 平级的兄弟节点（不是后代）。
            必须在根外——根有 overflow:hidden（裁大面板）+ backdrop-filter（让 fixed 后代以根为
            包含块、定位偏移）。兄弟节点不受这两者影响：大面板不裁、坐标正确。
            width/height:0 不占位、不挡点击；z-index 高于编辑器本体。
            注意：host 仍是编辑器后代，业务侧若在 host 之上的祖先链有 transform/filter 等创建
            包含块的属性，fixed 会以那个祖先为定位包含块、host 缩水到祖先尺寸——这影响
            「需要铺满视口的全屏遮罩」（如 antd Image preview）。这类场景应让浮层走 antd 默认
            的 document.body（FooterFilesList 的 Image 已这样改），不挂 host。
            host 仍承接技能面板/智能体下拉/ReportUrl 下拉/Steps 预览等「相对触发点定位」的浮层——
            它们用绝对/fixed + 计算坐标定位，不依赖 host 自身的 viewport 尺寸。 */}
            <div className="rte-popup-host" data-theme={editorTheme} ref={setPopupHost} />
            {/* message(toast)宿主不在此渲染：它由上方 useLayoutEffect 创建并 append 到
            ownerDocument.body 顶层，逃出业务祖先的 transform/filter stacking context，
            保证解析失败等错误 toast 永不被消费端 Modal 遮挡。 */}
          </FullscreenOverlayHostContext.Provider>
        </PopupHostContext.Provider>
      </App>
    </ConfigProvider>
  );
};

export type {
  StructuredInputProps,
  InputNode,
  NodeType,
  FooterReport,
  FooterFileUrl,
  ShopInfo,
  ShopDateValue,
  ShopListFetcher,
} from './types';
